import React, { useEffect, useState, useContext, useRef, useMemo, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { AuthContext } from "../../AuthContext";
import PageServices from "../../services/PageServices";
import CommonServices from '../../services/ComanServices';
import useAsync from '../../hooks/useAsync';
import OIPercentageGraph from './OIPercentageGraph';
import OIChartsPanel from './OIChartsPanel';
import SpotChartModal, { SPOT_CHART_MODAL_ID } from './SpotChartModal';
import GreeksGraph from './GreeksGraph';
import useStickyTableHeader from './useStickyTableHeader';
import { io } from 'socket.io-client';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
} from "recharts";
import { set } from "date-fns";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from 'chart.js';

// GFDL sends IV as a fraction (0.127 = 12.7%); show it as a percentage like other option chains.
// Values above 5 are assumed to be a percentage already.
const formatIV = (iv) => {
  const v = parseFloat(iv ?? 0) || 0;
  return (Math.abs(v) <= 5 ? v * 100 : v).toFixed(2);
};
// import { Bar } from 'recharts';

// ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);



// Strikes eligible for the 100% / 2nd / 3rd highest marks (AOC rule): resistance (CE) from
// SR_WINDOW_STRIKES strikes below spot upward, support (PE) from SR_WINDOW_STRIKES strikes
// above spot downward. Without a spot price every strike is eligible.
const SR_WINDOW_STRIKES = 2;

// "Select Strike" menu: strikes shown on each side of spot; "Max Strike" shows every
// available strike.
const MAX_STRIKE_OPTION = 'Max Strike';
const STRIKE_OPTIONS = ['7 Index', '10 Index', '15 Index', 'All Index', MAX_STRIKE_OPTION];

// Graph labels (AOC style): thousands → K, lakhs → L, crores → Cr, 2 decimals; small values as is.
const formatCompact = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  const abs = Math.abs(n);
  if (abs >= 10000000) return `${(n / 10000000).toFixed(2)}Cr`;
  if (abs >= 100000) return `${(n / 100000).toFixed(2)}L`;
  if (abs >= 1000) return `${(n / 1000).toFixed(2)}K`;
  return `${Math.round(n * 100) / 100}`;
};
const strikesInSrWindow = (rows, spot) => {
  if (!spot || !rows?.length) return { ceRows: rows || [], peRows: rows || [] };
  const strikes = rows.map((r) => Number(r.strikePrice));
  const below = strikes.filter((k) => k < spot).sort((a, b) => b - a);
  const above = strikes.filter((k) => k > spot).sort((a, b) => a - b);
  const ceFrom = below[SR_WINDOW_STRIKES - 1] ?? below[below.length - 1] ?? -Infinity;
  const peTo = above[SR_WINDOW_STRIKES - 1] ?? above[above.length - 1] ?? Infinity;
  return {
    ceRows: rows.filter((r) => Number(r.strikePrice) >= ceFrom),
    peRows: rows.filter((r) => Number(r.strikePrice) <= peTo),
  };
};

function Optionchain({ searchValue, onTradingChange, onTradingChangetwo }) {
  const [data, setData] = useState([]);
  const [oldData, setOldData] = useState([]);
  const [showFutureGraph, setShowFutureGraph] = useState(false);
  const { isLoggedIn, logout } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  // Percentage graph (AOC-style % of the strongest strike) for the clicked strike
  const [oiPctGraph, setOiPctGraph] = useState({ points: [] });
  const [oiPctGraphLoading, setOiPctGraphLoading] = useState(false);
  const oiPctRequestRef = useRef(0);
  const [oiChartsInstrument, setOiChartsInstrument] = useState(null); // strike shown in the OI Charts popup
  const [selectedStrikePrice, setSelectedStrikePrice] = useState(null);
  const [greekChainData, setGreekChainData] = useState([]);
  const [intraDay, setIntraDay] = useState('')
  const [spotPrice, setSpotPrice] = useState(0);
  const [trendSpotPrice, setTrendSpotPrice] = useState(0);
  const [spotChange, setSpotChange] = useState(0); // spot − previous trading day's 15:30 close (from the API)
  const [highestCEOIChange, setHighestCEOIChange] = useState(0);
  const [secondHighestCEOIChange, setSecondHighestCEOIChange] = useState(0);
  const [thirdHighestCEOIChange, setThirdHighestCEOIChange] = useState(0);

  const [highestCEOI, setHighestCEOI] = useState(0);
  const [secondHighestCEOI, setSecondHighestCEOI] = useState(0);
  const [thirdHighestCEOI, setThirdHighestCEOI] = useState(0);

  const [highestCEVolume, setHighestCEVolume] = useState(0);
  const [secondHighestCEVolume, setSecondHighestCEVolume] = useState(0);
  const [thirdHighestCEVolume, setThirdHighestCEVolume] = useState(0);
  const [highestPEOIChange, setHighestPEOIChange] = useState(0);
  const [secondHighestPEOIChange, setSecondHighestPEOIChange] = useState(0);
  const [thirdHighestPEOIChange, setThirdHighestPEOIChange] = useState(0);
  const [highestPEOI, setHighestPEOI] = useState(0);
  const [secondHighestPEOI, setSecondHighestPEOI] = useState(0);
  const [thirdHighestPEOI, setThirdHighestPEOI] = useState(0);
  const [highestPEVolume, setHighestPEVolume] = useState(0);
  const [secondHighestPEVolume, setSecondHighestPEVolume] = useState(0);
  const [thirdHighestPEVolume, setThirdHighestPEVolume] = useState(0);

  const [resistanceLevel, setResistanceLevel] = useState(null);
  const [supportLevel, setSupportLevel] = useState(null);
  const [futurePrice, setFutrePrice] = useState(0);
  const [previousHistory, setPreviousHistory] = useState([]);
  const [support, setSupport] = useState(0);
  const [resistance, setResistance] = useState(0);
  const [support2, setSupport2] = useState(0);
  const [resistance2, setResistance2] = useState(0);

  const [futureSupport, setFutureSupport] = useState(0);
  const [futureResistance, setFutureResistance] = useState(0);
  const [futureSupport2, setFutureSupport2] = useState(0);
  const [futureResistance2, setFutureResistance2] = useState(0);
  const [date, setDate] = useState(0);
  const [weeklyUpper, setWeeklyUpper] = useState(0);
  const [weeklyDown, setWeeklyDown] = useState(0);
  const [day, setDay] = useState(null);
  const [firstOptionChain, setFirstOptionChain] = useState([]);
  // 9:30 AM reversal values (AOC "Average S/R (9:30 AM)"): Resistance(K) = the reversal
  //   L(K) = spot + PE(K) − CE(next strike up)
  // averaged over 09:15–09:29 (`averageReversal` from the API). A strike without an average
  // uses the fixed 9:30 chain + 9:30 spot, and a strike missing there too (the feed only
  // records strikes around the morning spot) the live chain + live spot.
  // Support(K) = Resistance(previous strike). Rows are the live chain's strikes.
  const reversal930 = useMemo(() => {
    const ltp = (row, side) => Number(row?.[side]?.LastTradePrice) || 0;
    const sorted = (list) => [...(list || [])].sort((a, b) => Number(a.strikePrice) - Number(b.strikePrice));
    // "Next strike up" is the next strike shown in the table (e.g. +1000 for GOLD, whose
    // in-between strikes are hidden), so every source pairs the same two strikes.
    const pairValue = (list, refSpot, strike, nextStrike) => {
      const row = list.find((r) => Number(r.strikePrice) === strike);
      const next = list.find((r) => Number(r.strikePrice) === nextStrike);
      return row && next && refSpot && ltp(row, 'PE') && ltp(next, 'CE')
        ? refSpot + ltp(row, 'PE') - ltp(next, 'CE')
        : null;
    };
    const rows = sorted(data);
    const firstRows = sorted(firstOptionChain?.data);
    const firstSpot = firstOptionChain?.spot?.LastTradePrice;
    const averages = firstOptionChain?.averageReversal || {};
    const averageNext = firstOptionChain?.averageReversalNext || {};
    const resistanceAt = (i) => {
      if (!rows[i] || !rows[i + 1]) return null;
      const strike = Number(rows[i].strikePrice);
      const nextStrike = Number(rows[i + 1].strikePrice);
      if (averages[strike] != null && Number(averageNext[strike]) === nextStrike) return Number(averages[strike]);
      return pairValue(firstRows, firstSpot, strike, nextStrike) ?? pairValue(rows, spotPrice, strike, nextStrike);
    };
    return { rows, resistanceAt };
  }, [data, firstOptionChain, spotPrice]);

  // Strike shown in red (AOC style): the strike whose 9:30 band holds the live spot,
  // 9:30 Support(K) ≤ spot ≤ 9:30 Resistance(K). Starting at the strike at/below spot:
  // while its Resistance is below spot the red box moves up a strike, while its Support is
  // above spot it moves down. Stepping from spot (never scanning the whole chain) keeps
  // stray values from illiquid far strikes out. Falls back to the strike at/below spot.
  const spotStrike = useMemo(() => {
    const { rows, resistanceAt } = reversal930;
    if (!spotPrice || !rows.length) return null;

    const above = rows.findIndex((row) => Number(row.strikePrice) > spotPrice);
    let i = above === -1 ? rows.length - 1 : Math.max(above - 1, 0);
    const start = i;
    const MAX_STEPS = 5;
    for (let step = 0; step < MAX_STEPS; step++) {
      const resistance = resistanceAt(i);
      const support = resistanceAt(i - 1);
      if (resistance !== null && resistance < spotPrice && i + 1 < rows.length) i += 1;
      else if (support !== null && support > spotPrice && i - 1 >= 0) i -= 1;
      else break;
      if (Math.abs(i - start) > MAX_STEPS) break;
    }
    return Number(rows[i].strikePrice);
  }, [reversal930, spotPrice]);

  // Spot Chart levels (AOC style), centred on the red strike K0: resistance lines for K0 and
  // the two strikes above, support lines for K0 and the two below, at their 9:30 values.
  const spotChartLevels = useMemo(() => {
    const { rows, resistanceAt } = reversal930;
    const base = rows.findIndex((row) => Number(row.strikePrice) === spotStrike);
    if (base === -1) return [];
    const levels = [];
    for (let i = base; i <= base + 2; i++) {
      const price = resistanceAt(i);
      if (price !== null) levels.push({ kind: 'R', strike: Number(rows[i].strikePrice), price });
    }
    for (let i = base; i >= base - 2 && i >= 1; i--) {
      const price = resistanceAt(i - 1);
      if (price !== null) levels.push({ kind: 'S', strike: Number(rows[i].strikePrice), price });
    }
    return levels.map((l) => ({ ...l, price: Math.round(l.price * 100) / 100 }));
  }, [reversal930, spotStrike]);
  const [open, setOpen] = useState(false);
  const [selectedGreek, setSelectedGreek] = useState('');
  const [showTable, setShowTable] = useState(false);
  const [fullChain, setFullChain] = useState([]); // all strikes of the latest chain response
  const [oiPercentageGraphPE, setOiPercentageGraphPE] = useState('');
  const [oiPercentageGraphCE, setOiPercentageGraphCE] = useState('');
  const toggleDropdown = () => {
    setOpen(!open);
  };

  const styles = {
    dropdown: {
      position: 'relative',
      display: 'inline-block',
    },
    dropbtn: {
      backgroundColor: '#f77c22',
      color: 'white',
      padding: '8px 16px',
      fontSize: '16px',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      marginBottom: '10px',
    },
    dropdownContent: {
      position: 'absolute',
      backgroundColor: '#f77c22',
      minWidth: '160px',
      boxShadow: '0px 8px 16px rgba(0,0,0,0.2)',
      borderRadius: '8px',
      overflow: 'hidden',
      zIndex: 10, // above the sticky table header cells (z-index 3), below the sidebar / popups
      marginTop: '5px',
    },
    dropdownItem: {
      color: 'white',
      padding: '12px 16px',
      textDecoration: 'none',
      display: 'block',
      textAlign: 'center',
      fontWeight: 'bold',
      borderBottom: '1px solid white',
      cursor: 'pointer',
    },
    lastItem: {
      borderBottom: 'none',
    },
    hoverItem: {
      backgroundColor: '#e46c15',
    }
  };
  const [selectedGreeks, setSelectedGreeks] = useState([]);
  const greekitems = ['GAMMA', 'VEGA', 'THETA', 'DELTA', 'RESET'];
  const handleGreekSelect = (item) => {
    if (item === 'RESET') {
      setSelectedGreeks([]); // clear all on reset
    } else if (!selectedGreeks.includes(item)) {
      setSelectedGreeks(prev => [...prev, item]); // add only if not already selected
    }
    toggleDropdown();
  };
  const [activeButtons, setActiveButtons] = useState([]);

  const toggleButton = (buttonName) => {
    if (activeButtons.includes(buttonName)) {
      setActiveButtons(activeButtons.filter(name => name !== buttonName)); // remove button
    } else {
      setActiveButtons([...activeButtons, buttonName]); // add button
    }
  };

  // GFDL server info — logged to the browser console on page load
  useEffect(() => {
    PageServices.getServerInfo()
      .then((res) => console.log('GFDL ServerInfo:', res.data))
      .catch((err) => console.error('GFDL ServerInfo failed:', err.response?.data || err.message));
  }, []);

  useEffect(() => {
    const fetchDay = async () => {
      try {
        const response = await CommonServices.getDay();
        const dayData = response.data;
        setDay(dayData);

        const price = spotPrice; // aapka actual price yahan set karo
        const X = Math.sqrt(365 / dayData.weeklyDay);  // root nikal diya
        const Y = dayData.indiaVix;
        const Z = Y / X;  // Y divided by X

        const down = price - (price * Z / 100);
        const upper = price + (price * Z / 100);

        setWeeklyDown(down);
        setWeeklyUpper(upper);

      } catch (err) {
        console.error("Error fetching day data:", err);
      }
    };

    fetchDay();
  }, [spotPrice]);

  const [strikeDropdownOpen, setStrikeDropdownOpen] = useState(false);
  const tableBoxRef = useRef(null);
  const headerRow1Ref = useRef(null);
  useStickyTableHeader(tableBoxRef, headerRow1Ref);
  // Probable Swing High / Low columns (Settings → showColoum): shown unless switched off, and
  // updated as soon as the setting changes.
  const [showSwing, setShowSwing] = useState(() => localStorage.getItem('showColoum') !== 'false');
  useEffect(() => {
    const sync = () => setShowSwing(localStorage.getItem('showColoum') !== 'false');
    window.addEventListener('otw:setting', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('otw:setting', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  // Columns on each side of the strike column: OI Chg, OI, Volume, LTP, the swing column (or its
  // empty placeholder when switched off), plus the optional IV, IV/&/Y and Greeks columns.
  const sideColumns = 5
    + (activeButtons.includes('IV') ? 1 : 0) + (activeButtons.includes('IV/&/Y') ? 1 : 0)
    + ['GAMMA', 'VEGA', 'THETA', 'DELTA'].filter((g) => selectedGreeks.includes(g)).length;
  const [selectedStrike, setSelectedStrike] = useState('');
  // console.log("selectedStrike", selectedStrike)
  const toggleStrikeDropdown = () => {
    setStrikeDropdownOpen(!strikeDropdownOpen);
  };

  const handleStrikeSelect = (value) => {
    setSelectedStrike(value);
    setStrikeDropdownOpen(false);
    // Optional: Perform actions based on selection
    // console.log('Selected Strike:', value);
  };


  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [rangeOptions, setRangeOptions] = useState([]);
  const [callOI, setCallOI] = useState(0);
  const [putOI, setPutOI] = useState(0);
  const [callOIChange, setCallOIChange] = useState(0);
  const [putOIChange, setPutOIChange] = useState(0);
  const [dataCallPutOIChange, setDataCallPutOIChange] = useState([]);
  const [dataCallPutOI, setDataCallPutOI] = useState([]);
  // new vega feature
  const [currentVegaCall, setCurrentVegaCall] = useState([]);
  const [currentStrikeCountCall, setCurrentStrikeCountCall] = useState([]);
  const [oldStrikeCountCall, setOldStrikeCountCall] = useState(0);
  const [oldVegaCall, setOldVegaCall] = useState(0);
  const [vegaCallDiff, setVegaCallDiff] = useState(0);
  const [strikeCountCallDiff, setStrikeCountCallDiff] = useState(0);
  const [currentVegaPut, setCurrentVegaPut] = useState([]);
  const [currentStrikeCountPut, setCurrentStrikeCountPut] = useState([]);
  const [oldStrikeCountPut, setOldStrikeCountPut] = useState(0);
  const [oldVegaPut, setOldVegaPut] = useState(0);
  const [vegaPutDiff, setVegaPutDiff] = useState(0);
  const [strikeCountPutDiff, setStrikeCountPutDiff] = useState(0);
  const [currentVegaPutDiff, setCurrentVegaPutDiff] = useState(0);
  const [currentStrikeCountPutDiff, setCurrentStrikeCountPutDiff] = useState(0);
  const [oldVegaPutDiff, setOldVegaPutDiff] = useState(0);
  const [oldStrikeCountPutDiff, setOldStrikeCountPutDiff] = useState(0);
  const [vegaCallAndPutDiff, setVegaCallAndPutDiff] = useState(0);
  const [strikeCountCallAndPutDiff, setStrikeCountCallAndPutDiff] = useState(0);
  const [currentGammaCall, setCurrentGammaCall] = useState(0);
  const [currentDeltaCall, setCurrentDeltaCall] = useState(0);
  const [currentThetaCall, setCurrentThetaCall] = useState(0);
  const [currentGammaPut, setCurrentGammaPut] = useState(0);
  const [currentDeltaPut, setCurrentDeltaPut] = useState(0);
  const [currentThetaPut, setCurrentThetaPut] = useState(0);
  const [oldGammaCall, setOldGammaCall] = useState(0);
  const [oldDeltaCall, setOldDeltaCall] = useState(0);
  const [oldThetaCall, setOldThetaCall] = useState(0);
  const [oldGammaPut, setOldGammaPut] = useState(0);
  const [oldDeltaPut, setOldDeltaPut] = useState(0);
  const [oldThetaPut, setOldThetaPut] = useState(0);
  const [gammaCallDiff, setGammaCallDiff] = useState(0);
  const [deltaCallDiff, setDeltaCallDiff] = useState(0);
  const [thetaCallDiff, setThetaCallDiff] = useState(0);

  const [gammaPutDiff, setGammaPutDiff] = useState(0);
  const [deltaPutDiff, setDeltaPutDiff] = useState(0);
  const [thetaPutDiff, setThetaPutDiff] = useState(0);

  const [currentGammaPutDiff, setCurrentGammaPutDiff] = useState(0);
  const [currentDeltaPutDiff, setCurrentDeltaPutDiff] = useState(0);
  const [currentThetaPutDiff, setCurrentThetaPutDiff] = useState(0);
  // OLD: PUT - CALL
  const [oldGammaPutDiff, setOldGammaPutDiff] = useState(0);
  const [oldDeltaPutDiff, setOldDeltaPutDiff] = useState(0);
  const [oldThetaPutDiff, setOldThetaPutDiff] = useState(0);

  // DIFF: PUT - CALL (current vs old diffs)
  const [gammaCallAndPutDiff, setGammaCallAndPutDiff] = useState(0);
  const [deltaCallAndPutDiff, setDeltaCallAndPutDiff] = useState(0);
  const [thetaCallAndPutDiff, setThetaCallAndPutDiff] = useState(0);
  const [showGraph, setShowGraph] = useState(false);
  const [activeTab, setActiveTab] = useState("Delta");
  const [showFutureOI, setShowFutureOI] = useState(true);
  const [showFuturePrice, setShowFuturePrice] = useState(true);
  const [showSpotPrice, setShowSpotPrice] = useState(true);
  const [showFutureOIChange, setShowFutureOIChange] = useState(false);
  const [futureData, setFutureData] = useState([]);
  const futureDataSymbolRef = useRef(null); // symbol the loaded futureData belongs to
  const sampleData = [
    {
      time: "09:40AM", deltaCE: 6.7, deltaPE: 5.0, gammaCE: 0.8, gammaPE: 0.6,
      thetaCE: -1.2, thetaPE: -1.0,
      vegaCE: 1.5, vegaPE: 1.3,
    },
    {
      time: "10:00AM", deltaCE: 6.5, deltaPE: 5.3, gammaCE: 0.7, gammaPE: 0.5,
      thetaCE: -1.1, thetaPE: -0.9,
      vegaCE: 1.4, vegaPE: 1.2,
    },
    {
      time: "10:20AM", deltaCE: 6.3, deltaPE: 5.5, gammaCE: 0.7, gammaPE: 0.5,
      thetaCE: -1.1, thetaPE: -0.9,
      vegaCE: 1.4, vegaPE: 1.2,
    },
    {
      time: "10:40AM", deltaCE: 6.0, deltaPE: 5.6, gammaCE: 0.7, gammaPE: 0.5,
      thetaCE: -1.1, thetaPE: -0.9,
      vegaCE: 1.4, vegaPE: 1.2,
    },
    {
      time: "11:00AM", deltaCE: 5.9, deltaPE: 5.7, gammaCE: 0.7, gammaPE: 0.5,
      thetaCE: -1.1, thetaPE: -0.9,
      vegaCE: 1.4, vegaPE: 1.2,
    },
    {
      time: "11:20AM", deltaCE: 5.8, deltaPE: 5.8, gammaCE: 0.7, gammaPE: 0.5,
      thetaCE: -1.1, thetaPE: -0.9,
      vegaCE: 1.4, vegaPE: 1.2,
    },
    {
      time: "11:40AM", deltaCE: 5.7, deltaPE: 5.9, gammaCE: 0.7, gammaPE: 0.5,
      thetaCE: -1.1, thetaPE: -0.9,
      vegaCE: 1.4, vegaPE: 1.2,
    },
    {
      time: "12:00PM", deltaCE: 5.6, deltaPE: 6.0, gammaCE: 0.7, gammaPE: 0.5,
      thetaCE: -1.1, thetaPE: -0.9,
      vegaCE: 1.4, vegaPE: 1.2,
    },
    {
      time: "12:20PM", deltaCE: 5.5, deltaPE: 6.1, gammaCE: 0.7, gammaPE: 0.5,
      thetaCE: -1.1, thetaPE: -0.9,
      vegaCE: 1.4, vegaPE: 1.2,
    },
    {
      time: "12:40PM", deltaCE: 5.6, deltaPE: 6.0, gammaCE: 0.7, gammaPE: 0.5,
      thetaCE: -1.1, thetaPE: -0.9,
      vegaCE: 1.4, vegaPE: 1.2,
    },
    {
      time: "01:00PM", deltaCE: 5.7, deltaPE: 5.9, gammaCE: 0.7, gammaPE: 0.5,
      thetaCE: -1.1, thetaPE: -0.9,
      vegaCE: 1.4, vegaPE: 1.2,
    },
    {
      time: "01:20PM", deltaCE: 5.8, deltaPE: 5.8, gammaCE: 0.7, gammaPE: 0.5,
      thetaCE: -1.1, thetaPE: -0.9,
      vegaCE: 1.4, vegaPE: 1.2,
    },
    {
      time: "01:40PM", deltaCE: 5.9, deltaPE: 5.7, gammaCE: 0.7, gammaPE: 0.5,
      thetaCE: -1.1, thetaPE: -0.9,
      vegaCE: 1.4, vegaPE: 1.2,
    },
    {
      time: "02:00PM", deltaCE: 6.0, deltaPE: 5.6, gammaCE: 0.7, gammaPE: 0.5,
      thetaCE: -1.1, thetaPE: -0.9,
      vegaCE: 1.4, vegaPE: 1.2,
    },
    {
      time: "02:20PM", deltaCE: 6.1, deltaPE: 5.5, gammaCE: 0.7, gammaPE: 0.5,
      thetaCE: -1.1, thetaPE: -0.9,
      vegaCE: 1.4, vegaPE: 1.2,
    },
    {
      time: "02:40PM", deltaCE: 6.2, deltaPE: 5.4, gammaCE: 0.7, gammaPE: 0.5,
      thetaCE: -1.1, thetaPE: -0.9,
      vegaCE: 1.4, vegaPE: 1.2,
    },
    { time: "03:00PM", deltaCE: 6.3, deltaPE: 5.3 },
    { time: "03:20PM", deltaCE: 6.4, deltaPE: 5.2 },
    { time: "03:40PM", deltaCE: 6.5, deltaPE: 5.1 },
  ];
  const transformData = (data) => {
    return data.map((d) => ({
      ...d,
      deltaDiff: +(d.deltaCE - d.deltaPE).toFixed(2),
      gammaDiff: +(d.gammaCE - d.gammaPE).toFixed(2),
      thetaDiff: +(d.thetaCE - d.thetaPE).toFixed(2),
      vegaDiff: +(d.vegaCE - d.vegaPE).toFixed(2),
    }));
  };
  const getYAxisConfig = (data, tab) => {
    const ce = data.map((d) => d[`${tab.toLowerCase()}CE`] ?? 0);
    const pe = data.map((d) => d[`${tab.toLowerCase()}PE`] ?? 0);
    const diff = data.map((d) => d[`${tab.toLowerCase()}Diff`] ?? 0);

    const maxValue = Math.ceil(Math.max(...ce, ...pe));
    const minValue = Math.floor(Math.min(...diff));

    const ticks = [];
    for (let i = minValue; i <= maxValue; i++) {
      ticks.push(i);
    }

    return {
      domain: [minValue, maxValue],
      ticks,
    };
  };
  const transformedData = useMemo(() => transformData(sampleData), []);
  const yAxis = useMemo(() => getYAxisConfig(transformedData, activeTab), [transformedData, activeTab]);

  // end feature
  const isInitialRangeSet = useRef(false);
  useEffect(() => {
    if (data && data.length > 0) {
      const prices = data.map(item => item.strikePrice);
      setRangeOptions(prices);

      if (!isInitialRangeSet.current) {
        setRangeStart(prices[0]);
        setRangeEnd(prices[prices.length - 1]);
        isInitialRangeSet.current = true; // Mark as initialized
      }
    }
  }, [data]);


  useEffect(() => {
    calculateOpenInterestChange();
  }, [rangeStart, rangeEnd, data]);

  const calculateOpenInterestChange = () => {
    if (!data || data.length === 0) return;

    // Filter kar rahe strike prices ke range ke andar
    const filteredData = data.filter(item => {
      const strike = parseFloat(item.strikePrice);
      return strike >= parseFloat(rangeStart) && strike <= parseFloat(rangeEnd);
    });

    // CE and PE OpenInterestChange ka total
    let totalCallOIChange = 0;
    let totalPutOIChange = 0;
    let totalCallOI = 0;
    let totalPutOI = 0;

    filteredData.forEach(item => {
      if (item.CE) {
        totalCallOIChange += item.CE.OpenInterestChange || 0;
        totalCallOI += item.CE.OpenInterest || 0;
      }
      if (item.PE) {
        totalPutOIChange += item.PE.OpenInterestChange || 0;
        totalPutOI += item.PE.OpenInterest || 0;
      }
    });

    setCallOIChange(totalCallOIChange / 100000);
    setPutOIChange(totalPutOIChange / 100000);
    setCallOI(totalCallOI / 100000);
    setPutOI(totalPutOI / 100000);




    setDataCallPutOIChange([
      { name: "CALL", value: totalCallOIChange / 1000 },
      { name: "PUT", value: totalPutOIChange / 1000 }
    ]);
    setDataCallPutOI([
      { name: "CALL", value: totalCallOI / 1000 },
      { name: "PUT", value: totalPutOI / 1000 }
    ]);
  };
  const fetchFutureData = async () => {
    try {
      const symbolToken = localStorage.getItem('stock');
      // Data for another symbol must not be shown while the new one loads
      if (futureDataSymbolRef.current !== symbolToken) {
        futureDataSymbolRef.current = null;
        setFutureData([]);
      }
      const res = await PageServices.getFutureAndSpotData(symbolToken);

      const future = res.data?.futureData || [];
      const spot = res.data?.spotData || [];

      if (!future.length || !spot.length) {
        console.warn("Either future or spot data is empty.");
        setFutureData([]);
        return;
      }

      // Index spot rows by minute so each future point only checks nearby rows
      // (same match as before: first spot row, in API order, within 1 minute).
      const MINUTE = 60 * 1000;
      const spotByMinute = new Map();
      spot.forEach((sp, index) => {
        const time = new Date(sp.timestamp).getTime();
        const bucket = Math.floor(time / MINUTE);
        if (!spotByMinute.has(bucket)) spotByMinute.set(bucket, []);
        spotByMinute.get(bucket).push({ index, time });
      });
      const findSpot = (futureTime) => {
        const bucket = Math.floor(futureTime / MINUTE);
        let best = null;
        for (let b = bucket - 1; b <= bucket + 1; b++) {
          for (const entry of spotByMinute.get(b) || []) {
            if (Math.abs(entry.time - futureTime) <= MINUTE && (!best || entry.index < best.index)) {
              best = entry;
            }
          }
        }
        return best ? spot[best.index] : undefined;
      };

      const timeFormatter = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' });

      const mergedData = future.map(f => {
        const futureTimestamp = new Date(f.timestamp);

        const matchedSpot = findSpot(futureTimestamp.getTime());

        // "HH:mm" in IST (toISOString() gave UTC, e.g. 04:03 for 09:33)
        const timeString = timeFormatter.format(futureTimestamp);

        return {
          time: timeString,
          futurePrice: f.FutruePrice?.LastTradePrice || 0,
          futureOI: f.FutruePrice?.OpenInterest || 0,
          futureOIChange: f.FutruePrice?.OpenInterestChange || 0,
          spotPrice: matchedSpot?.spotPrice?.LastTradePrice || 0,
        };
      });

      // 🔁 Sort merged data by time (increasing order: 09:15 → 10:58); "HH:mm" sorts as a string
      const sortedData = mergedData.sort((a, b) => (a.time < b.time ? -1 : a.time > b.time ? 1 : 0));

      // Ignore a response for a symbol that is no longer selected
      if (localStorage.getItem('stock') !== symbolToken) return;
      futureDataSymbolRef.current = symbolToken;
      setFutureData(sortedData);

    } catch (error) {
      console.error("Error fetching future & spot data:", error);
    }
  };

  // Preload the Future Price Graph so the modal opens with data; clicking still refreshes it.
  useEffect(() => {
    fetchFutureData();
  }, []);



  const fetchData = async () => {
    try {

      // count ema 
      function calculateEMA(values, period = 3) {
        let ema = values[2]?.Low; // Start with the first value as the initial EMA
        const smoothingFactor = 2 / (period + 1); // Smoothing factor (multiplier)

        for (let i = 1; i >= 0; i--) {
          ema = (smoothingFactor * values[i].Low) + ((1 - smoothingFactor) * ema);
        }

        return ema;
      }
      let response
      // The symbol this request is for. A response that arrives after the user has switched
      // to another symbol is dropped (and leaves the spinner on), so one symbol's data is
      // never shown under another.
      const requestSymbol = localStorage.getItem("stock");
      const isStale = () => localStorage.getItem("stock") !== requestSymbol;

      if (requestSymbol === "GOLD" || requestSymbol === "SILVER" || requestSymbol === 'CRUDEOIL' || requestSymbol === "COPPER" || requestSymbol === "NATURALGAS") {

        response = await PageServices.getComodityOptionChain({
          symbolToken: requestSymbol,
        });
        if (isStale()) return;
      } else {

        response = await PageServices.getOptionChain({
          symbolToken: requestSymbol,
        });
        if (isStale()) return;
        // console.log("ALL response", response);
        response.formattedData.sort((x, y) => parseFloat(x.strikePrice) - parseFloat(y.strikePrice));
      }


      PageServices.getDataForSuportandRessitans(requestSymbol).then((response) => {
        if (!isStale()) setFirstOptionChain(response);
      }).catch((error) => {
        console.error('Error fetching data for support and ressitance:', error);
      });

      // Ensure response.sub and response.future exist before accessing them
      // consolconsole.loge.log("setFutrePrice",response)
      setSpotPrice(response?.spotPrice?.LastTradePrice ?? 0);
      setTrendSpotPrice(response?.spotPrice?.LastTradePrice ?? 0);
      setSpotChange(Number(response?.spotPrice?.Change ?? 0));
      setFutrePrice(response?.futurePrice?.LastTradePrice ?? 0);
      onTradingChange(response?.historical);
      setPreviousHistory(response?.historical);
      const currentDate = new Date();

      // Extract the components from the date
      const month = currentDate.toLocaleString('default', { month: 'short' });  // Short month name (e.g., "Dec")
      const day = String(currentDate.getDate()).padStart(2, '0');  // Day with leading zero if necessary
      const year = currentDate.getFullYear();  // Full year (e.g., 2024)
      const hours = String(currentDate.getHours()).padStart(2, '0');  // Hours with leading zero
      const minutes = String(currentDate.getMinutes()).padStart(2, '0');  // Minutes with leading zero
      const seconds = String(currentDate.getSeconds()).padStart(2, '0');  // Seconds with leading zero

      // Combine them into the desired format
      const formattedDate = `${month} ${day} ${hours}:${minutes}:${seconds}`;

      setDate(formattedDate);

      // Intraday trend needs the 3 latest candles; without them keep the previous trend
      // instead of throwing (which used to skip the table update below).
      const minuteCandles = response?.historicalResponseEx?.Result || [];
      if (minuteCandles.length >= 3) {
        const emaLow = calculateEMA(minuteCandles)
        if (minuteCandles[0].Close > emaLow) {
          onTradingChangetwo('Bullish');
        } else {
          onTradingChangetwo('Bearish');
        }
      }
      if (Array.isArray(response?.formattedData)) {
        setFullChain(response.formattedData); // every strike of the chain, for the Greeks totals
        // GOLD / SILVER show every 1000 strike only. With a "Select Strike" choice the strike
        // count means shown strikes, so the in-between strikes (the feed also lists 100 / 500
        // steps) are dropped before taking the rows around spot. Without a choice the default
        // is unchanged: 40 rows of the full chain, then the 1000 filter below.
        const stock = localStorage.getItem("stock");
        const chainRows = (stock === "GOLD" || stock === "SILVER") && selectedStrike
          ? response.formattedData.filter((item) => Number(item.strikePrice) % 1000 === 0)
          : response.formattedData;
        let middleValues = chainRows
        let midleIndex;
        chainRows.some((item, index) => {
          if (item?.strikePrice >= response?.spotPrice?.LastTradePrice) {
            midleIndex = index;
            return true;  // This will stop the iteration
          }
          return false;
        });
        // Spot above every strike → centre on the last strike; spot missing → the middle strike.
        // (An undefined index used to slice an empty table.)
        if (midleIndex === undefined) {
          midleIndex = response?.spotPrice?.LastTradePrice
            ? chainRows.length - 1
            : Math.floor(chainRows.length / 2);
        }

        // response.formattedData.forEach((item, index) => {
        //   if (item.strikePrice >= response.spotPrice?.LastTradePrice) {
        //     midleIndex = index;
        //     return;  // This will stop the iteration
        //   }
        // });

        let upperIndex;
        let lowerIndex;

        if (selectedStrike === MAX_STRIKE_OPTION) {
          // "Max Strike": every available strike
          upperIndex = 0;
          lowerIndex = middleValues.length;
        } else if (!selectedStrike && (localStorage.getItem("stock") === "GOLD" || localStorage.getItem("stock") === "SILVER" || localStorage.getItem('stock') === 'CRUDEOIL' || localStorage.getItem("stock") === "COPPER" || localStorage.getItem("stock") === "NATURALGAS")) {

          upperIndex = Math.max(midleIndex - 40, 0);  // Prevent negative indices
          lowerIndex = Math.min(midleIndex + 40, middleValues.length);
        } else {
          let strikeRange = 15; // default

          if (selectedStrike === "7 Index") strikeRange = 7;
          else if (selectedStrike === "10 Index") strikeRange = 10;
          else if (selectedStrike === "15 Index") strikeRange = 15;
          else if (selectedStrike === "All Index") strikeRange = 20;
          // console.log("strikeRange", strikeRange)
          upperIndex = Math.max(midleIndex - strikeRange, 0);  // Prevent negative indices
          lowerIndex = Math.min(midleIndex + strikeRange, middleValues.length);
        }
        const finalData = middleValues.slice(upperIndex, lowerIndex)
        // Rows actually shown in the table (GOLD / SILVER show every 1000 only); the
        // highest-value ranking below uses these so a hidden strike is never the 100%.
        let displayedData = finalData;

        if (localStorage.getItem("stock") === "GOLD" || localStorage.getItem("stock") === "SILVER") {

          // Strike interval shown (AOC): 1000 for GOLD and SILVER. Strikes in between are hidden.
          const step = 1000;
          const filteredArray = finalData.filter(value => Number(value.strikePrice) % step === 0);
          displayedData = filteredArray;
          setData(filteredArray);
        } else {
          if (localStorage.getItem('reverseTable') === "true") {
            finalData.reverse();
          }
          setData(finalData);

        }



        // Find the highest and second-highest values for each column — AOC rule, by spot:
        //   resistance (CE OI / OI change / volume): strikes from 2 strikes below spot upward
        //   support    (PE OI / OI change / volume): strikes from 2 strikes above spot downward
        // A strike outside its window is not ranked, so it is never marked 100% / 2nd / 3rd
        // (its percentage is still shown against the window's highest, e.g. 108%).
        const { ceRows, peRows } = strikesInSrWindow(displayedData, response?.spotPrice?.LastTradePrice);
        const ceOpenInterestChanges = [
          ...new Set(
            ceRows.map(
              (item) => item.CE?.OpenInterestChange ?? 0
            )
          ),
        ].sort((a, b) => b - a);

        const ceOpenInterests = [
          ...new Set(
            ceRows.map((item) => item.CE?.OpenInterest ?? 0)
          ),
        ].sort((a, b) => b - a);
        const ceTotalQtyTraded = [
          ...new Set(
            ceRows.map((item) => item.CE?.TotalQtyTraded ?? 0)
          ),
        ].sort((a, b) => b - a);
        const peOpenInterestChanges = [
          ...new Set(
            peRows.map(
              (item) => item.PE?.OpenInterestChange ?? 0
            )
          ),
        ].sort((a, b) => b - a);
        const peOpenInterests = [
          ...new Set(
            peRows.map((item) => item.PE?.OpenInterest ?? 0)
          ),
        ].sort((a, b) => b - a);
        const peTotalQtyTraded = [
          ...new Set(
            peRows.map((item) => item.PE?.TotalQtyTraded ?? 0)
          ),
        ].sort((a, b) => b - a);

        const setValues = (values, setHighest, setSecond, setThird) => {
          setHighest(values[0] || 0);
          setSecond(values[1] || 0);
          setThird(values[2] || 0);
        };

        setValues(
          ceOpenInterestChanges,
          setHighestCEOIChange,
          setSecondHighestCEOIChange,
          setThirdHighestCEOIChange
        );
        setValues(
          ceOpenInterests,
          setHighestCEOI,
          setSecondHighestCEOI,
          setThirdHighestCEOI
        );
        setValues(
          ceTotalQtyTraded,
          setHighestCEVolume,
          setSecondHighestCEVolume,
          setThirdHighestCEVolume
        );
        setValues(
          peOpenInterestChanges,
          setHighestPEOIChange,
          setSecondHighestPEOIChange,
          setThirdHighestPEOIChange
        );
        setValues(
          peOpenInterests,
          setHighestPEOI,
          setSecondHighestPEOI,
          setThirdHighestPEOI
        );
        setValues(
          peTotalQtyTraded,
          setHighestPEVolume,
          setSecondHighestPEVolume,
          setThirdHighestPEVolume
        );

        const maxCEOI = Math.max(
          ...response.formattedData.map((item) => item.CE?.OpenInterest || 0)
        );
        const maxPEOI = Math.max(
          ...response.formattedData.map((item) => item.PE?.OpenInterest || 0)
        );
        const resistance = response.formattedData.find(
          (item) => item.CE?.OpenInterest === maxCEOI
        )?.strikePrice;
        const support = response.formattedData.find(
          (item) => item.PE?.OpenInterest === maxPEOI
        )?.strikePrice;
        setResistanceLevel(resistance);
        setSupportLevel(support);
      } else {
        // If formattedData is not an array, set loading to false
        setLoading(false);
        // console.error("Invalid formattedData:", response.formattedData);
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };
  // new feature code
  // new feature code
  React.useEffect(() => {

    fetchHistoricalData();

  }, []);

  const fetchHistoricalData = async () => {


    try {
      const response = await PageServices.getOldData({
        date: "28-Jul-2025", // You can make this dynamic as needed
        time: "15:30",       // You can make this dynamic as needed
        symbolToken: localStorage.getItem("stock"),
      });
      // console.log("Historical Data Response:", response);

      if (response && response.data) {

        const spot = await PageServices.getOldSpot({
          date: "11-Aug-2025", // You can make this dynamic as needed
          time: "15:30",       // You can make this dynamic as needed
          symbolToken: localStorage.getItem("stock"),
        })

        if (spot && spot.data) {
          // console.log("spot",spot)
          // setSpotPrice(spot?.data?.spotPrice?.LastTradePrice);
        }

        const options = response.data; // Extract the options array
        // setSpotPrice(24945);
        setOldData(options);

        // console.log(response?.data?.spotPrice?.LastTradePrice)

        // Find the highest and second-highest values for each column
        const ceOpenInterestChanges = [...new Set(options.map(item => item.CE?.OpenInterestChange ?? 0))].sort((a, b) => b - a);
        const ceOpenInterests = [...new Set(options.map(item => item.CE?.OpenInterest ?? 0))].sort((a, b) => b - a);
        const ceTotalQtyTraded = [...new Set(options.map(item => item.CE?.TotalQtyTraded ?? 0))].sort((a, b) => b - a);
        const peOpenInterestChanges = [...new Set(options.map(item => item.PE?.OpenInterestChange ?? 0))].sort((a, b) => b - a);
        const peOpenInterests = [...new Set(options.map(item => item.PE?.OpenInterest ?? 0))].sort((a, b) => b - a);
        const peTotalQtyTraded = [...new Set(options.map(item => item.PE?.TotalQtyTraded ?? 0))].sort((a, b) => b - a);

        const setValues = (values, setHighest, setSecond, setThird) => {
          setHighest(values[0] || 0);
          setSecond(values[1] || 0);
          setThird(values[2] || 0);
        };

        setValues(ceOpenInterestChanges, setHighestCEOIChange, setSecondHighestCEOIChange, setThirdHighestCEOIChange);
        setValues(ceOpenInterests, setHighestCEOI, setSecondHighestCEOI, setThirdHighestCEOI);
        setValues(ceTotalQtyTraded, setHighestCEVolume, setSecondHighestCEVolume, setThirdHighestCEVolume);
        setValues(peOpenInterestChanges, setHighestPEOIChange, setSecondHighestPEOIChange, setThirdHighestPEOIChange);
        setValues(peOpenInterests, setHighestPEOI, setSecondHighestPEOI, setThirdHighestPEOI);
        setValues(peTotalQtyTraded, setHighestPEVolume, setSecondHighestPEVolume, setThirdHighestPEVolume);

        const maxCEOI = Math.max(...options.map(item => item.CE?.OpenInterest || 0));
        const maxPEOI = Math.max(...options.map(item => item.PE?.OpenInterest || 0));
        const resistance = options.find(item => item.CE?.OpenInterest === maxCEOI)?.strikePrice;
        const support = options.find(item => item.PE?.OpenInterest === maxPEOI)?.strikePrice;

        setResistanceLevel(resistance);
        setSupportLevel(support);
      } else {
        setData([]); // Handle case where options are not present
      }
    } catch (error) {
      console.error('Error fetching data:', error.message);
    } finally {
      setLoading(false);
    }
  };
  // Step 1: Prepare the data
  useEffect(() => {
    if (!data || data.length === 0) return;

    const ceOptions = data
      .filter(item => item?.CE && item.strikePrice)
      .map(item => ({
        strikePrice: parseInt(item.strikePrice),
        change: item.CE?.OpenInterestChange ?? 0,
        OI: item.CE?.OpenInterest ?? 0,
        volume: item.CE?.TotalQtyTraded ?? 0,
        vega: item.CE?.Vega ?? 0,
        gamma: item.CE?.Gamma ?? 0,
        delta: item.CE?.Delta ?? 0,
        theta: item.CE?.Theta ?? 0,
      }));

    function getTopStrikesBy(key) {
      const sorted = [...ceOptions].sort((a, b) => b[key] - a[key]);
      const topThree = sorted.slice(0, 3);
      const highestVal = topThree[0]?.[key] ?? 0;
      const validTops = topThree
        .filter(item => (item[key] / highestVal) * 100 >= 75)
        .map(item => item.strikePrice);
      return validTops;
    }

    const changeTops = getTopStrikesBy("change");
    const oiTops = getTopStrikesBy("OI");
    const volumeTops = getTopStrikesBy("volume");

    const allTopStrikes = Array.from(new Set([
      ...changeTops,
      ...oiTops,
      ...volumeTops
    ]));

    if (allTopStrikes.length < 2) {
      console.log("Not enough valid top CE strikes to calculate range.");
      return;
    }

    const minStrike = Math.min(...allTopStrikes);
    const maxStrike = Math.max(...allTopStrikes);

    const strikesInRange = ceOptions.filter(
      item => item.strikePrice >= minStrike && item.strikePrice <= maxStrike
    );

    const strikeCount = strikesInRange.length;
    const totalVega = strikesInRange.reduce((sum, item) => sum + item.vega, 0);
    const totalGamma = strikesInRange.reduce((sum, item) => sum + item.gamma, 0);
    const totalDelta = strikesInRange.reduce((sum, item) => sum + item.delta, 0);
    const totalTheta = strikesInRange.reduce((sum, item) => sum + item.theta, 0);

    setCurrentStrikeCountCall(strikeCount);
    setCurrentVegaCall(totalVega);
    setCurrentGammaCall(totalGamma);
    setCurrentDeltaCall(totalDelta);
    setCurrentThetaCall(totalTheta);

  }, [data]);
  

  useEffect(() => {
    if (!data || data.length === 0) return;

    const peOptions = data
      .filter(item => item?.PE && item.strikePrice)
      .map(item => ({
        strikePrice: parseInt(item.strikePrice),
        change: item.PE?.OpenInterestChange ?? 0,
        OI: item.PE?.OpenInterest ?? 0,
        volume: item.PE?.TotalQtyTraded ?? 0,
        vega: item.PE?.Vega ?? 0,
        gamma: item.PE?.Gamma ?? 0,
        delta: item.PE?.Delta ?? 0,
        theta: item.PE?.Theta ?? 0,
      }));

    function getTopStrikesByPE(key) {
      const sorted = [...peOptions].sort((a, b) => b[key] - a[key]);
      const topThree = sorted.slice(0, 3);
      const highestVal = topThree[0]?.[key] ?? 0;
      const validTops = topThree
        .filter(item => (item[key] / highestVal) * 100 >= 75)
        .map(item => item.strikePrice);
      return validTops;
    }

    const changeTopsPE = getTopStrikesByPE("change");
    const oiTopsPE = getTopStrikesByPE("OI");
    const volumeTopsPE = getTopStrikesByPE("volume");

    const allTopStrikesPE = Array.from(new Set([
      ...changeTopsPE,
      ...oiTopsPE,
      ...volumeTopsPE
    ]));

    if (allTopStrikesPE.length < 2) {
      // console.log("Not enough valid top PE strikes to calculate range.");
      return;
    }

    const minStrikePE = Math.min(...allTopStrikesPE);
    const maxStrikePE = Math.max(...allTopStrikesPE);

    const strikesInRangePE = peOptions.filter(
      item => item.strikePrice >= minStrikePE && item.strikePrice <= maxStrikePE
    );

    const strikeCountPE = strikesInRangePE.length;
    const totalVegaPE = strikesInRangePE.reduce((sum, item) => sum + item.vega, 0);
    const totalGammaPE = strikesInRangePE.reduce((sum, item) => sum + item.gamma, 0);
    const totalDeltaPE = strikesInRangePE.reduce((sum, item) => sum + item.delta, 0);
    const totalThetaPE = strikesInRangePE.reduce((sum, item) => sum + item.theta, 0);

    setCurrentStrikeCountPut(strikeCountPE);
    setCurrentVegaPut(totalVegaPE);
    setCurrentGammaPut(totalGammaPE);
    setCurrentDeltaPut(totalDeltaPE);
    setCurrentThetaPut(totalThetaPE);

  }, [data]);
 

  // old data vega
  useEffect(() => {
    if (!oldData || oldData.length === 0) return;

    const ceOptions = oldData
      .filter(item => item?.CE && item.strikePrice)
      .map(item => ({
        strikePrice: parseInt(item.strikePrice),
        change: item.CE?.OpenInterestChange ?? 0,
        OI: item.CE?.OpenInterest ?? 0,
        volume: item.CE?.TotalQtyTraded ?? 0,
        vega: item.CE?.Vega ?? 0,
        gamma: item.CE?.Gamma ?? 0,
        delta: item.CE?.Delta ?? 0,
        theta: item.CE?.Theta ?? 0,
      }));

    function getTopStrikesBy(key) {
      const sorted = [...ceOptions].sort((a, b) => b[key] - a[key]);
      const topThree = sorted.slice(0, 3);
      const highestVal = topThree[0]?.[key] ?? 0;
      return topThree
        .filter(item => (item[key] / highestVal) * 100 >= 75)
        .map(item => item.strikePrice);
    }

    const changeTops = getTopStrikesBy("change");
    const oiTops = getTopStrikesBy("OI");
    const volumeTops = getTopStrikesBy("volume");

    const allTopStrikes = Array.from(new Set([...changeTops, ...oiTops, ...volumeTops]));

    if (allTopStrikes.length < 2) {
      console.log("Not enough valid top CE strikes to calculate range.");
      return;
    }

    const minStrike = Math.min(...allTopStrikes);
    const maxStrike = Math.max(...allTopStrikes);

    const strikesInRange = ceOptions.filter(
      item => item.strikePrice >= minStrike && item.strikePrice <= maxStrike
    );

    const strikeCount = strikesInRange.length;
    const totalVega = strikesInRange.reduce((sum, item) => sum + item.vega, 0);
    const totalGamma = strikesInRange.reduce((sum, item) => sum + item.gamma, 0);
    const totalDelta = strikesInRange.reduce((sum, item) => sum + item.delta, 0);
    const totalTheta = strikesInRange.reduce((sum, item) => sum + item.theta, 0);

    setOldStrikeCountCall(strikeCount);
    setOldVegaCall(totalVega);
    setOldGammaCall(totalGamma);
    setOldDeltaCall(totalDelta);
    setOldThetaCall(totalTheta);

  }, [oldData]);
  useEffect(() => {
    if (!oldData || oldData.length === 0) return;

    const peOptions = oldData
      .filter(item => item?.PE && item.strikePrice)
      .map(item => ({
        strikePrice: parseInt(item.strikePrice),
        change: item.PE?.OpenInterestChange ?? 0,
        OI: item.PE?.OpenInterest ?? 0,
        volume: item.PE?.TotalQtyTraded ?? 0,
        vega: item.PE?.Vega ?? 0,
        gamma: item.PE?.Gamma ?? 0,
        delta: item.PE?.Delta ?? 0,
        theta: item.PE?.Theta ?? 0,
      }));

    function getTopStrikesBy(key) {
      const sorted = [...peOptions].sort((a, b) => b[key] - a[key]);
      const topThree = sorted.slice(0, 3);
      const highestVal = topThree[0]?.[key] ?? 0;
      return topThree
        .filter(item => (item[key] / highestVal) * 100 >= 75)
        .map(item => item.strikePrice);
    }

    const changeTops = getTopStrikesBy("change");
    const oiTops = getTopStrikesBy("OI");
    const volumeTops = getTopStrikesBy("volume");

    const allTopStrikes = Array.from(new Set([...changeTops, ...oiTops, ...volumeTops]));

    if (allTopStrikes.length < 2) {
      // console.log("⚠️ Not enough valid top PE strikes to calculate range.");
      return;
    }

    const minStrike = Math.min(...allTopStrikes);
    const maxStrike = Math.max(...allTopStrikes);

    const strikesInRange = peOptions.filter(
      item => item.strikePrice >= minStrike && item.strikePrice <= maxStrike
    );

    const strikeCount = strikesInRange.length;
    const totalVega = strikesInRange.reduce((sum, item) => sum + item.vega, 0);
    const totalGamma = strikesInRange.reduce((sum, item) => sum + item.gamma, 0);
    const totalDelta = strikesInRange.reduce((sum, item) => sum + item.delta, 0);
    const totalTheta = strikesInRange.reduce((sum, item) => sum + item.theta, 0);

    setOldStrikeCountPut(strikeCount);
    setOldVegaPut(totalVega);
    setOldGammaPut(totalGamma);
    setOldDeltaPut(totalDelta);
    setOldThetaPut(totalTheta);

    // console.log("👉 Old PE Range Metrics:", {
    //   strikeCount,
    //   totalVega,
    //   totalGamma,
    //   totalDelta,
    //   totalTheta,
    //   range: [minStrike, maxStrike],
    // });
  }, [oldData]);

  useEffect(() => {
    // Ensure all required values are available
    if (
      typeof currentVegaCall === 'number' &&
      typeof oldVegaCall === 'number' &&
      typeof currentStrikeCountCall === 'number' &&
      typeof oldStrikeCountCall === 'number' &&
      typeof currentGammaCall === 'number' &&
      typeof oldGammaCall === 'number' &&
      typeof currentDeltaCall === 'number' &&
      typeof oldDeltaCall === 'number' &&
      typeof currentThetaCall === 'number' &&
      typeof oldThetaCall === 'number'
    ) {
      const vegaDiff = currentVegaCall - oldVegaCall;
      const strikeDiff = currentStrikeCountCall - oldStrikeCountCall;

      setVegaCallDiff(vegaDiff);
      setStrikeCountCallDiff(strikeDiff);
      setGammaCallDiff(currentGammaCall - oldGammaCall);
      setDeltaCallDiff(currentDeltaCall - oldDeltaCall);
      setThetaCallDiff(currentThetaCall - oldThetaCall);

      // console.log("📊 CE Vega Difference:", vegaDiff);
      // console.log("📊 CE Strike Count Difference:", strikeDiff);
    }
  }, [currentVegaCall, oldVegaCall, currentStrikeCountCall, oldStrikeCountCall, currentGammaCall, oldGammaCall, currentDeltaCall, oldDeltaCall, currentThetaCall, oldThetaCall]);

  useEffect(() => {
    // Ensure all values are available and are numbers
    if (
      typeof currentVegaPut === 'number' &&
      typeof oldVegaPut === 'number' &&
      typeof currentStrikeCountPut === 'number' &&
      typeof oldStrikeCountPut === 'number' &&
      typeof currentGammaPut === 'number' &&
      typeof oldGammaPut === 'number' &&
      typeof currentDeltaPut === 'number' &&
      typeof oldDeltaPut === 'number' &&
      typeof currentThetaPut === 'number' &&
      typeof oldThetaPut === 'number'
    ) {
      const vegaDiff = currentVegaPut - oldVegaPut;
      const strikeDiff = currentStrikeCountPut - oldStrikeCountPut;

      setVegaPutDiff(vegaDiff);
      setStrikeCountPutDiff(strikeDiff);
      setGammaPutDiff(currentGammaPut - oldGammaPut);
      setDeltaPutDiff(currentDeltaPut - oldDeltaPut);
      setThetaPutDiff(currentThetaPut - oldThetaPut);

      // console.log("✅ PE Vega Difference:", vegaDiff);
      // console.log("✅ PE Strike Count Difference:", strikeDiff);
    }
  }, [currentVegaPut, oldVegaPut, currentStrikeCountPut, oldStrikeCountPut, currentGammaPut, oldGammaPut, currentDeltaPut, oldDeltaPut, currentThetaPut, oldThetaPut]);
  useEffect(() => {
    // Ensure all values are numbers before doing math
    const vegaCall = Number(currentVegaCall);
    const vegaPut = Number(currentVegaPut);
    const strikeCall = Number(currentStrikeCountCall);
    const strikePut = Number(currentStrikeCountPut);
    const gammaDiff = Number(currentGammaPut) - Number(currentGammaCall);
    const deltaDiff = Number(currentDeltaPut) - Number(currentDeltaCall);
    const thetaDiff = Number(currentThetaPut) - Number(currentThetaCall);
    setCurrentVegaPutDiff(vegaPut - vegaCall);
    setCurrentStrikeCountPutDiff(strikePut - strikeCall);
    setCurrentGammaPutDiff(gammaDiff);
    setCurrentDeltaPutDiff(deltaDiff);
    setCurrentThetaPutDiff(thetaDiff);
  }, [currentVegaCall, currentVegaPut, currentStrikeCountCall, currentStrikeCountPut, currentGammaPut, currentGammaCall, currentDeltaPut, currentDeltaCall, currentThetaPut, currentThetaCall]);

  useEffect(() => {
    // Ensure all values are numbers before doing math
    const vegaCall = Number(oldVegaCall);
    const vegaPut = Number(oldVegaPut);
    const strikeCall = Number(oldStrikeCountCall);
    const strikePut = Number(oldStrikeCountPut);
    const gammaCall = Number(oldGammaCall);
    const gammaPut = Number(oldGammaPut);
    const deltaCall = Number(oldDeltaCall);
    const deltaPut = Number(oldDeltaPut);
    const thetaCall = Number(oldThetaCall);
    const thetaPut = Number(oldThetaPut);


    setOldVegaPutDiff(vegaPut - vegaCall);
    setOldStrikeCountPutDiff(strikePut - strikeCall);
    setOldGammaPutDiff(gammaPut - gammaCall);
    setOldDeltaPutDiff(deltaPut - deltaCall);
    setOldThetaPutDiff(thetaPut - thetaCall);
  }, [oldVegaCall, oldVegaPut, oldStrikeCountCall, oldStrikeCountPut, oldGammaCall, oldGammaPut, oldDeltaCall, oldDeltaPut, oldThetaCall, oldThetaPut]);

  useEffect(() => {
    // Ensure all values are numbers before doing math
    const vegaCall = Number(vegaCallDiff);
    const vegaPut = Number(vegaPutDiff);
    const strikeCall = Number(strikeCountCallDiff);
    const strikePut = Number(strikeCountPutDiff);
    const gammaCall = Number(gammaCallDiff);
    const gammaPut = Number(gammaPutDiff);
    const deltaCall = Number(deltaCallDiff);
    const deltaPut = Number(deltaPutDiff);
    const thetaCall = Number(thetaCallDiff);
    const thetaPut = Number(thetaPutDiff);
    setVegaCallAndPutDiff(vegaPut - vegaCall);
    setStrikeCountCallAndPutDiff(strikePut - strikeCall);
    setGammaCallAndPutDiff(gammaPut - gammaCall);
    setDeltaCallAndPutDiff(deltaPut - deltaCall);
    setThetaCallAndPutDiff(thetaPut - thetaCall);
  }, [vegaCallDiff, vegaPutDiff, strikeCountCallDiff, strikeCountPutDiff, gammaCallDiff, gammaPutDiff, deltaCallDiff, deltaPutDiff, thetaCallDiff, thetaPutDiff]);


  // end code
  // The timer always calls the latest fetchData (so a changed strike range applies). A tick
  // is skipped only while the previous request is under 10 s old, so one slow or hung
  // request can never stop the updates.
  const fetchDataRef = useRef(fetchData);
  fetchDataRef.current = fetchData;
  const fetchStartedAtRef = useRef(0);
  const runFetch = useCallback(async () => {
    if (fetchStartedAtRef.current && Date.now() - fetchStartedAtRef.current < 10000) return;
    const startedAt = Date.now();
    fetchStartedAtRef.current = startedAt;
    try {
      await fetchDataRef.current();
    } finally {
      if (fetchStartedAtRef.current === startedAt) fetchStartedAtRef.current = 0;
    }
  }, []);

  useEffect(() => {
    let intervalId;
    // New symbol: drop the previous symbol's chain and 9:30 data at once, show the spinner,
    // and fetch now even if the previous symbol's request is still in flight (its response
    // is discarded by fetchData).
    setLoading(true);
    setData([]);
    setFirstOptionChain([]);
    fetchStartedAtRef.current = 0;
    runFetch(); // show data right away instead of after the first interval
    if (localStorage.getItem("stock") === "GOLD" || localStorage.getItem("stock") === "SILVER") {
      intervalId = setInterval(runFetch, 15000);
    } else {
      intervalId = setInterval(runFetch, 5000);
    }

    return () => clearInterval(intervalId);
  }, [searchValue, runFetch]);

  // Live updates over the backend's Socket.IO: every option-chain or spot tick pushed for
  // this symbol reloads the chain (at most once a second), so OI, OI change, volume and
  // prices follow the feed. The timer above stays as the fallback if the socket is down.
  useEffect(() => {
    const symbol = (localStorage.getItem("stock") || "NIFTY").toUpperCase();
    let origin;
    try {
      origin = new URL(process.env.REACT_APP_API_BASE_URL).origin;
    } catch (_) {
      return undefined;
    }
    const socket = io(origin, { transports: ["websocket"], reconnection: true });
    let lastRefresh = 0;
    let timer = null;
    const refresh = () => {
      const wait = 1000 - (Date.now() - lastRefresh);
      if (wait <= 0) {
        lastRefresh = Date.now();
        runFetch();
      } else if (!timer) {
        timer = setTimeout(() => {
          timer = null;
          lastRefresh = Date.now();
          runFetch();
        }, wait);
      }
    };
    socket.on("connect", () => socket.emit("subscribe:symbol", { symbol }));
    socket.on("optionchain:update", refresh);
    socket.on("spot:update", refresh);
    return () => {
      clearTimeout(timer);
      socket.disconnect();
    };
  }, [searchValue, runFetch]);

  // Re-slice immediately when the strike range changes.
  useEffect(() => {
    if (selectedStrike) runFetch();
  }, [selectedStrike, runFetch]);


  // const onHistoricsClick = (data) => {
  //   PageServices.getHistorydata({
  //     instrumentIdentifier: data.InstrumentIdentifier,
  //     date: "28-jul-2025",
  //     symbolToken: localStorage.getItem("stock"),
  //   }).then((response) => {

  //     const processData = (data) => {
  //       return data
  //         ?.map((entry) => {
  //           const dateObj = new Date(entry.LastTradeTime * 1000);
  //           return {
  //             ...entry,
  //             LastTradeTime: dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  //             _timestamp: dateObj.getTime(), // Add a numeric timestamp for sorting
  //           };
  //         })
  //         .sort((a, b) => a._timestamp - b._timestamp); // Sort by timestamp (earliest to latest)
  //     };

  //     const dataSet = processData(response.data);
  //     console.log("dataSet",dataSet)
  //     setProcessedData(dataSet);
  //   });
  // };
  const onHistoricsClick = (data) => {
    console.log(">>>>>data", data)
    const today = new Date();

    // Format date as "6-aug-2025"
    const formattedDate = `${today.getDate()}-${today.toLocaleString('en-US', { month: 'short' }).toLowerCase()}-${today.getFullYear()}`;
    const identifier = data.InstrumentIdentifier || "";
    const parts = identifier.split("_");
    // Example parts: ["OPTIDX", "NIFTY", "14AUG2025", "PE", "24450"]

    if (parts.length >= 5) {
      const optionType = parts[3]; // "PE" or "CE"
      const strikePrice = parts[4]; // "24450"

      if (optionType === "PE") {
        setOiPercentageGraphPE(`${optionType} | ${strikePrice}`);
        setOiPercentageGraphCE(''); // reset CE
      } else if (optionType === "CE") {
        setOiPercentageGraphCE(`${optionType} | ${strikePrice}`);
        setOiPercentageGraphPE(''); // reset PE
      }

    }
    loadOiPctGraph(data.InstrumentIdentifier);
  };

  // Today's session for the clicked strike; responses from an older click are ignored.
  const loadOiPctGraph = (instrumentIdentifier) => {
    const requestId = ++oiPctRequestRef.current;
    setOiPctGraphLoading(true);
    setOiPctGraph({ points: [] });
    PageServices.getOiStrikeGraph({
      instrumentIdentifier,
      symbolToken: localStorage.getItem("stock"),
    }).then((response) => {
      if (requestId === oiPctRequestRef.current) setOiPctGraph(response?.data || { points: [] });
    }).catch(() => {
      if (requestId === oiPctRequestRef.current) setOiPctGraph({ points: [] });
    }).finally(() => {
      if (requestId === oiPctRequestRef.current) setOiPctGraphLoading(false);
    });
  };

  const fetchGreekChainData = (instrument) => {
    PageServices.getOptionGreekChain({
      instrument,
    })
      .then((response) => {
        const processData = response.map((entry) => ({
          time: new Date(entry.LastTradeTime * 1000).toLocaleTimeString(),
          OI: entry.OpenInterest,
          OIChange: entry.OIChange,
          Volume: entry.Volume,
        }));
        setGreekChainData(processData);
      })
      .catch((error) => console.log(error));
  };
  // OI Charts popup: the panel loads (and keeps refreshing) the clicked strike's charts.
  const onHistoricsMultipleClick = (data) => {
    setOiChartsInstrument(data.InstrumentIdentifier);
  };

  const formatTime = (timeString) => {
    const [hours, minutes, seconds] = timeString.split(":");
    const suffix = hours >= 12 ? "PM" : "AM";
    const adjustedHours = hours % 12 || 12;
    return `${adjustedHours}:${minutes} ${suffix}`;
  };

  // Function to process data fetched from the API
  const processGreekChainData = (data) => {
    return data.map((entry) => ({
      time: formatTime(entry.time),
      OI: entry.OI,
      OIChange: entry.OIChange,
      Volume: entry.Volume,
    }));
  };

  const OptionChainChart = ({ data }) => (
    <LineChart
      width={950}
      height={500}
      data={processGreekChainData(data)}
      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
    >
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="time" />
      <YAxis />
      <Tooltip />
      <Legend />
      <Line type="monotone" dataKey="OI" stroke="#0000FF" />
      <Line type="monotone" dataKey="OIChange" stroke="#00FF00" />
      <Line type="monotone" dataKey="Volume" stroke="#FF0000" />
    </LineChart>
  );

  // Modal trigger
  const onModalOpen = (instrument) => {
    fetchGreekChainData(instrument); // Fetch data when the modal opens
  };

  const getSpotPriceRow = () => {
    if (spotPrice) {


      return (
        <tr key="spot-price" className="oc-spot-row">
          <td colSpan={sideColumns}>{shiftTopBottom().CE}{intradayTrainding()}</td>
          {/* Banner centred on the strike column of this row (it overflows onto the side cells) */}
          <td className="strike-pric spot-price oc-spot-cell">
            <button
              onClick={fetchFutureData}
              data-bs-toggle="modal"
              data-bs-target="#futurpricegraph"
              className="oc-spot-pill"
            >
              {/* {localStorage.getItem('showFuturePrice')==='true' || null ? `F : ${futurePrice}` : ' '} * Spot : {spotPrice} * F-S : {(futurePrice - spotPrice).toFixed(1)} */}
              F : {futurePrice} * Spot : {trendSpotPrice} ({spotChange > 0 ? '+' : ''}{spotChange.toFixed(2)}) * F-S : {(futurePrice - spotPrice).toFixed(1)}
            </button>
          </td>
          <td colSpan={sideColumns}>{shiftTopBottom().PE}{intradayTrainding()}</td>
        </tr>
      );
    }
  };
  // const getNearestValues = () => {
  //   const nearestIndex = data.findIndex(
  //     (item) => item.strikePrice >= spotPrice
  //   );
  //   const start = Math.max(0, nearestIndex - 2); // 2 values before the nearest
  //   const end = Math.min(data.length, nearestIndex + 3); // 2 values after the nearest
  //   return data.slice(start, end);
  // };

  const formatNumber = (value) => {
    const lotSize        = Number(localStorage.getItem('expiry')) || 1;
    const showDataInLots = localStorage.getItem('showDataInLots') === 'true';
    const showQt         = localStorage.getItem('showQt')         === 'true';
    const showNumbers    = localStorage.getItem('showNumbers')    === 'true';

    // Divide by lot size when either lots toggle is enabled
    const useValue = (showDataInLots || showQt) ? value / lotSize : value;

    // showNumbers: return raw divided value without K/L formatting
    if (showNumbers) return useValue;

    // Format with K / L / Cr suffix (applied to the divided value)
    const abs = Math.abs(useValue);
    if (abs >= 10000000) return `${(useValue / 10000000).toFixed(2)}Cr`;
    if (abs >= 100000)   return `${(useValue / 100000).toFixed(2)}L`;
    if (abs >= 1000)     return `${(useValue / 1000).toFixed(2)}k`;
    return parseFloat(useValue.toFixed(2));
  };

  const calculatePercentage = (value, highestValue) => {
    if (highestValue === 0) return 0;
    return ((value / highestValue) * 100).toFixed(2);
  };

  // const handleButtonClick = () => {
  //   setShowClock(true); // Show clock when the button is clicked
  // };



  const ivCalculate = (value) => {
    const iv = (spotPrice - value?.strikePrice)
    const tv = value?.CE?.LastTradePrice - iv
    return tv.toFixed(2);
  }
  const ivCalculatePE = (value) => {
    const iv = (value?.strikePrice - spotPrice)
    const tv = value?.CE?.LastTradePrice - iv
    return tv.toFixed(2);
  }


  const countCprVolume = (value) => {
    let totalCeOI = 0;
    let totalPeOI = 0;

    let obj = {};
    // let middleValues = value.slice(
    //   Math.max(0, Math.floor(value.length / 2) - 7),
    //   Math.min(value.length, Math.floor(value.length / 2) + 8)
    // );
    let middleValues = value


    // Iterate over the middleValues to sum the quantities
    middleValues?.forEach((item) => {
      totalCeOI += item?.CE?.TotalQtyTraded || 0;  // Add to totalCeOI, use 0 if undefined
      totalPeOI += item?.PE?.TotalQtyTraded || 0;  // Add to totalPeOI, use 0 if undefined
    });

    // Calculate PCR OI (Put/Call Ratio)
    const pcrOi = totalCeOI === 0 ? 0 : totalCeOI / totalPeOI;

    obj.totalCeOI = totalCeOI.toFixed(2);  // Fixed to 2 decimal places
    obj.totalPeOI = totalPeOI.toFixed(2);  // Fixed to 2 decimal places
    obj.pcrOi = pcrOi.toFixed(2);  // Fixed to 2 decimal places

    return obj;
  };


  const countPcrOIchg = (value) => {
    let totalCeOI = 0; // Initialize to 0 for accumulation
    let totalPeOI = 0; // Initialize to 0 for accumulation

    let obj = {};

    // Extract a portion of the array to work with
    // let middleValues = value.slice(
    //   Math.max(0, Math.floor(value.length / 2) - 7),
    //   Math.min(value.length, Math.floor(value.length / 2) + 8)
    // );

    let middleValues = value

    // Accumulate OpenInterestChange values for CE and PE
    middleValues?.forEach((item) => {
      totalCeOI += item?.CE?.OpenInterestChange || 0;  // Accumulate CE OpenInterestChange
      totalPeOI += item?.PE?.OpenInterestChange || 0;  // Accumulate PE OpenInterestChange
    });

    // Calculate the Put/Call Ratio (PCR) OI change (avoid division by 0)
    const pcrOi = totalCeOI === 0 ? 0 : totalPeOI / totalCeOI;

    // Format the result with two decimal places
    obj.totalCeOI = totalCeOI.toFixed(2);
    obj.totalPeOI = totalPeOI.toFixed(2);
    obj.pcrOi = pcrOi.toFixed(2);

    return obj;
  };


  const countPcrOI = (value) => {
    let totalCeOI = 0; // Initialize for accumulation
    let totalPeOI = 0; // Initialize for accumulation

    let obj = {};
    // let middleValues = value.slice(
    //   Math.max(0, Math.floor(value.length / 2) - 7),
    //   Math.min(value.length, Math.floor(value.length / 2) + 8)
    // );
    let middleValues = value

    // Accumulate OpenInterest for both CE and PE
    middleValues?.forEach((item) => {
      totalCeOI += item?.CE?.OpenInterest || 0;  // Add OpenInterest from CE
      totalPeOI += item?.PE?.OpenInterest || 0;  // Add OpenInterest from PE
    });

    // Calculate PCR OI (Put/Call Ratio)
    const pcrOi = totalCeOI === 0 ? 0 : totalPeOI / totalCeOI;

    // Format the result to two decimal places
    obj.totalCeOI = totalCeOI.toFixed(2);
    obj.totalPeOI = totalPeOI.toFixed(2);
    obj.pcrOi = pcrOi.toFixed(2);

    return obj;
  };


  const countUperHeader = (value) => {
    let upper = [];
    let lower = [];

    let upperSum = 0;
    let lowerSum = 0;
    let upperSumPE = 0;
    let lowerSumPE = 0;
    let ltpChg = 0;
    let ltpChgPE = 0;
    let oiChg = 0;
    let oiChgPE = 0;
    let itmTv = 0;
    let itmTvPE = 0;

    value?.forEach((item) => {
      ltpChg += item?.CE?.PriceChange;
      ltpChgPE += item?.PE?.PriceChange
      oiChg += item?.CE?.OpenInterestChange;
      oiChgPE += item?.PE?.OpenInterestChange
      // CE or PE can be missing for a strike — count it as 0 instead of crashing
      if (item.strikePrice < spotPrice) {
        upperSum += item?.CE?.LastTradePrice || 0;
        upperSumPE += item?.PE?.LastTradePrice || 0;
        upper.push(item);
      } else {
        lowerSum += item?.CE?.LastTradePrice || 0;
        lowerSumPE += item?.PE?.LastTradePrice || 0;
        lower.push(item);
      }
    });


    if (upperSum > lowerSum) {
      // ITM
      upper.forEach((item) => {
        const iv = (spotPrice - item?.strikePrice);
        const tv = item?.CE?.LastTradePrice - iv;
        itmTv += tv;
      });
    } else {
      // OTM
      lower.forEach((item) => {
        const iv = (spotPrice - item?.strikePrice);
        const tv = item?.CE?.LastTradePrice - iv;
        itmTv += tv;

      });
    }

    if (upperSumPE > lowerSumPE) {
      // ITM
      upper.forEach((item) => {
        const iv = (item?.strikePrice - spotPrice);
        const tv = item?.CE?.LastTradePrice - iv;
        itmTvPE += tv;
      });
    } else {
      // OTM
      lower.forEach((item) => {
        const iv = (item?.strikePrice - spotPrice);
        const tv = item?.CE?.LastTradePrice - iv;
        itmTvPE += tv;
      });
    }

    const oichagPePer = (oiChgPE * 100 / (oiChgPE + oiChg))
    const oichagPer = (oiChg * 100 / (oiChg + oiChgPE))

    const obj = {
      itmTv: itmTv.toFixed(2),
      itmTvPE: itmTvPE.toFixed(2),
      ltpChg: ltpChg.toFixed(2),
      ltpChgPE: ltpChgPE.toFixed(2),
      oichagPer: oichagPer.toFixed(2),
      oichagPePer: oichagPePer.toFixed(2)
    };

    return obj;
  };

  // Standard Normal CDF (N(x))
  function erf(x) {
    const a1 = 0.254829592;
    const a2 = -0.284496736;
    const a3 = 1.421413741;
    const a4 = -1.453152027;
    const a5 = 1.061405429;
    const p = 0.3275911;

    const sign = (x >= 0) ? 1 : -1;
    x = Math.abs(x);

    const t = 1.0 / (1.0 + p * x);
    const y = 1.0 - (((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t) * Math.exp(-x * x);

    return sign * y;
  }

  // Standard Normal CDF (N(x))
  function normCDF(x) {
    return (1.0 + erf(x / Math.sqrt(2))) / 2.0;
  }

  // Standard Normal PDF (N'(x))
  function normPDF(x) {
    return Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI);
  }

  // Black-Scholes Call Option Price
  function blackScholesCall(S, K, T, r, sigma) {
    if (S <= 0 || K <= 0 || T <= 0 || sigma <= 0) {
      return NaN;  // Invalid input, return NaN
    }

    const d1 = (Math.log(S / K) + (r + 0.5 * sigma * sigma) * T) / (sigma * Math.sqrt(T));
    const d2 = d1 - sigma * Math.sqrt(T);
    const callPrice = S * normCDF(d1) - K * Math.exp(-r * T) * normCDF(d2);
    return callPrice;
  }

  // Gamma (Γ) Calculation
  function calculateGamma(S, K, T, r, sigma) {
    if (S <= 0 || K <= 0 || T <= 0 || sigma <= 0) {
      return NaN;  // Invalid input, return NaN
    }

    const d1 = (Math.log(S / K) + (r + 0.5 * sigma * sigma) * T) / (sigma * Math.sqrt(T));
    const gamma = normPDF(d1) / (S * sigma * Math.sqrt(T));
    return gamma;
  }

  // Calculate Implied Volatility using Newton-Raphson method
  function impliedVolatility(S, K, T, r, marketPrice) {
    if (S <= 0 || K <= 0 || T <= 0 || marketPrice <= 0) {
      return NaN;  // Invalid input, return NaN
    }

    const tolerance = 1e-5;
    let sigma = 0.2; // Initial guess for IV
    let maxIter = 100;
    let iter = 0;

    while (iter < maxIter) {
      const price = blackScholesCall(S, K, T, r, sigma);
      if (isNaN(price)) {
        return NaN; // If price calculation fails, return NaN
      }

      const vega = calculateGamma(S, K, T, r, sigma) * S * Math.sqrt(T); // Vega is related to Gamma
      if (vega === 0) {
        return NaN; // Vega cannot be zero, return NaN
      }

      const diff = price - marketPrice;
      if (Math.abs(diff) < tolerance) break;
      sigma = sigma - diff / vega; // Newton-Raphson step

      iter++;
    }

    if (iter >= maxIter) {
      return NaN;  // If no convergence after max iterations, return NaN
    }

    return sigma; // Returns the implied volatility
  }


  const onStrikePriceClick = (item, index) => {
    setSelectedStrikePrice(item.strikePrice);

    const d = item?.PE?.LastTradePrice - data[index + 1]?.CE?.LastTradePrice;

    const m = item?.CE?.LastTradePrice - data[index - 1]?.PE?.LastTradePrice;
    const gamma = item?.CE?.Gamma || 1;
    const mP = (gamma * m) / 100;
    const dP = (gamma * d) / 100;
    setResistance((d + dP) + spotPrice);

    setSupport(spotPrice - (m + mP));

    // 9:30 AM values come only from the fixed 9:30 snapshot (strike and its neighbours),
    // so they do not move with the live chain.
    // Neighbours are the strikes shown next to this one in the table (as for the current
    // values above), looked up by strike in the 9:30 chain.
    const firstData = firstOptionChain?.data || [];
    const firstAt = (strike) => firstData.find((optionItem) => Number(optionItem.strikePrice) === Number(strike));
    const item2 = firstAt(item.strikePrice);
    const nextStrike = data[index + 1]?.strikePrice;
    const prevStrike = data[index - 1]?.strikePrice;
    const firstFuturePrice = firstOptionChain?.future?.LastTradePrice || futurePrice;

    const d2 = item2?.PE?.LastTradePrice - firstAt(nextStrike)?.CE?.LastTradePrice;
    const m2 = item2?.CE?.LastTradePrice - firstAt(prevStrike)?.PE?.LastTradePrice;

    // Spot tab: AOC's 09:15–09:29 averages when the API has them for this strike / the one
    // below, paired with the same neighbouring strike.
    const averages = firstOptionChain?.averageReversal || {};
    const averageNext = firstOptionChain?.averageReversalNext || {};
    const averageFor = (strike, next) => (strike != null && next != null
      && averages[Number(strike)] != null && Number(averageNext[Number(strike)]) === Number(next)
      ? Number(averages[Number(strike)])
      : null);
    const averageR = averageFor(item.strikePrice, nextStrike);
    const averageS = averageFor(prevStrike, item.strikePrice);
    setResistance2(averageR ?? d2 + firstOptionChain?.spot?.LastTradePrice);
    setSupport2(averageS ?? firstOptionChain?.spot?.LastTradePrice - m2);

    // future resistance and support
    const dF = item?.PE?.LastTradePrice - data[index + 1]?.CE?.LastTradePrice;

    const mF = item?.CE?.LastTradePrice - data[index - 1]?.PE?.LastTradePrice;

    const mPF = (item?.CE?.Gamma * mF) / 100;
    const dPF = (item?.CE?.Gamma * dF) / 100;

    setFutureResistance((dF + dPF) + futurePrice);

    setFutureSupport(futurePrice - (mF + mPF));

    setFutureResistance2(d2 + firstFuturePrice);
    setFutureSupport2(firstFuturePrice - m2);

  }
  // const onFutureStrikePriceClick = (item,index) => {

  //   setSelectedStrikePrice(item.strikePrice);

  //   const df = item?.PE?.LastTradePrice - data[index+1]?.CE?.LastTradePrice;

  //   const mf = item?.CE?.LastTradePrice - data[index-1]?.PE?.LastTradePrice;

  //   const mPF = (item?.CE?.Gamma * mf )/100;
  //   const dPF = (item?.CE?.Gamma * df )/100;

  //   setFutureResistance((dF +dPF) + futurePrice) ;

  //   setFutureSupport(futurePrice - (mF + mPF));

  //   const d2F = item2?.PE?.LastTradePrice - data[index+1]?.CE?.LastTradePrice;

  //   const m2F = item2?.CE?.LastTradePrice - data[index-1]?.PE?.LastTradePrice;

  //   setFutureResistance2(d2F + firstOptionChain?.future?.LastTradePrice) ;

  //   setFutureSupport2(firstOptionChain?.future?.LastTradePrice - m2F);

  // }

  const countResistans = (item, index) => {
    const d = item?.PE?.LastTradePrice - data[index + 1]?.CE?.LastTradePrice;

    const m = item?.CE?.LastTradePrice - data[index - 1]?.PE?.LastTradePrice;

    const resistance = d + spotPrice;
    const support = spotPrice - m;

    return {
      resistance,
      support
    }
  }

  // Strength of the label under each Probable Swing column, from the OI change of the two
  // sides: STRONG when this side's OI change is at least double the other side's (and
  // positive), otherwise NORMAL. Rows with no OI-change signal stay blank, as before.
  const swingStrength = (own, other) => (own > 0 && own >= 2 * other ? 'STRONG' : 'NORMAL');
  const hasSwingSignal = (item) => {
    const ce = item?.CE?.OpenInterestChange;
    const pe = item?.PE?.OpenInterestChange;
    return ce != null && pe != null && !(Number.isNaN(Number(ce)) || Number.isNaN(Number(pe)))
      && !(Number(ce) === 0 && Number(pe) === 0);
  };

  // Probable Swing High column: always a resistance (R) label.
  const countResistansAndSupport = (item, index) => {
    if (!hasSwingSignal(item)) return '';
    const strength = swingStrength(Number(item.CE.OpenInterestChange), Number(item.PE.OpenInterestChange));
    return strength ? <span style={{ color: '#fff', fontWeight: 'bold' }}>{strength} R &nbsp;</span> : '';
  }

  // Probable Swing Low column: always a support (S) label.
  const countSupportAndResistance = (item, index) => {
    if (!hasSwingSignal(item)) return '';
    const strength = swingStrength(Number(item.PE.OpenInterestChange), Number(item.CE.OpenInterestChange));
    return strength ? <span style={{ color: '#fff', fontWeight: 'bold' }}>{strength} S &nbsp;</span> : '';
  }

  const getBackgroundClass = (item) => {
    const result = countResistansAndSupport(item);

    switch (result) {
      case 'STRONG R':
        return 'bg-danger'; // red background
      case 'R':
        return 'bg-warning'; // yellow background
      case 'STRONG S':
        return 'bg-success'; // green background
      case 'S':
        return 'bg-info'; // light blue background
      default:
        return ''; // no background color
    }
  };


  const countPcrOiBStrike = (item) => {
    const result = item?.PE?.OpenInterest / item?.CE?.OpenInterest;

    if (!isFinite(result)) return NaN;  // Agar result Infinite ho, toh NaN return karo
    if (isNaN(result)) return 0.0;      // Agar result NaN ho, toh 0.0 return karo
    if (result < 0) return '0.0';       // never negative under the strike (AOC shows 0.0)

    return Math.round(result * 100) / 100; // Normal case ke liye rounded value return karo
  };

  const countPcrOiChgBStrike = (item) => {
    const result = item?.PE?.OpenInterestChange / item?.CE?.OpenInterestChange;

    if (!isFinite(result)) return 0.0;
    if (isNaN(result)) return NaN;
    if (result < 0) return '0.0'; // never negative under the strike (AOC shows 0.0)

    return Math.round(result * 100) / 100;
  };


  const formatNumber2 = (value) => {
    let useValue;

    if (value >= 100000) {
      return `${(value / 100000).toFixed(2)}L`; // Format as lakhs
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(2)}k`; // Format as thousands
    } else {
      return value; // Return as is
    }


  };

  const intradayTrainding = () => {
    let midleIndex;

    data.some((item, index) => {
      if (item?.strikePrice >= spotPrice) {
        midleIndex = index;
        return true;  // This will stop the iteration
      }
      return false;
    });

    const firstStrike = data[midleIndex - 1];
    const secondStrike = data[midleIndex];
    const thirdStrike = data[midleIndex + 1];

    const totalCeOI = firstStrike?.CE?.OpenInterest + secondStrike?.CE?.OpenInterest + thirdStrike?.CE?.OpenInterest
    const totalPeOI = firstStrike?.PE?.OpenInterest + secondStrike?.PE?.OpenInterest + thirdStrike?.PE?.OpenInterest

    // return totalCeOI>totalPeOI ? 'BEARISH':'BULLISH'
    const result = totalCeOI > totalPeOI ? 'BEARISH' : 'BULLISH';
    const style = {
      color: totalCeOI > totalPeOI ? 'red' : 'green',
      fontSize: '25px',
      fontWeight: 'bold'
    };

    return <div style={style}>{result}</div>;

  }

  intradayTrainding();
  let isSpotPrice;

  // const shiftTopBottom = () => {
  //   let middleIndex;

  //   // Find the first strikePrice >= spotPrice
  //   data.some((item, index) => {
  //     if (item?.strikePrice >= spotPrice) {
  //       middleIndex = index;
  //       return true;
  //     }
  //     return false;
  //   });

  //   // Flatten all CE values and store with their strikePrice
  //   const oiChangeList = data.map(item => ({
  //     strikePrice: item.strikePrice,
  //     value: item?.CE?.OpenInterestChange || 0,
  //   })).sort((a, b) => b.value - a.value);

  //   const oiList = data.map(item => ({
  //     strikePrice: item.strikePrice,
  //     value: item?.CE?.OpenInterest || 0,
  //   })).sort((a, b) => b.value - a.value);

  //   const volumeList = data.map(item => ({
  //     strikePrice: item.strikePrice,
  //     value: item?.CE?.TotalQtyTraded || 0,
  //   })).sort((a, b) => b.value - a.value);

  //   // Get top value (or top 3 if needed)
  //   const topOIChange = oiChangeList[0];
  //   const topOI = oiList[0];
  //   const topVolume = volumeList[0];
  //   const distances = [topOIChange, topOI, topVolume].map(entry => ({
  //     ...entry,
  //     diff: Math.abs(entry.strikePrice - spotPrice)
  //   }));

  //   // Find the nearest to spotPrice
  //   const nearest = distances.sort((a, b) => a.diff - b.diff)[0];
  //   const currentIndex = data.findIndex(item => item.strikePrice === nearest.strikePrice);
  //   let shiftFrom = null;

  //   for (let i = 0; i < data.length; i++) {
  //     const item = data[i];
  //     if (
  //       item?.CE?.OpenInterestChange === nearest.value ||
  //       item?.CE?.OpenInterest === nearest.value ||
  //       item?.CE?.TotalQtyTraded === nearest.value
  //     ) {
  //       if (i !== currentIndex) {
  //         shiftFrom = item.strikePrice;
  //         break;
  //       }
  //     }
  //   }

  //   // Decide SFB or SFT
  //   let result = '';
  //   if (shiftFrom !== null) {
  //     if (nearest.strikePrice > shiftFrom) {
  //       result = `SFB ${shiftFrom} → ${nearest.strikePrice}`;
  //     } else {
  //       result = `SFT ${shiftFrom} → ${nearest.strikePrice}`;
  //     }
  //   } else {
  //     result = `No shift detected`;
  //   }

  //   return result;
  // };
  const shiftTopBottom = () => {
    const detectShift = (optionType) => {
      // Flatten and sort lists for the given option type
      const oiChangeList = data.map(item => ({
        strikePrice: item.strikePrice,
        value: item?.[optionType]?.OpenInterestChange || 0,
      })).sort((a, b) => b.value - a.value);

      const oiList = data.map(item => ({
        strikePrice: item.strikePrice,
        value: item?.[optionType]?.OpenInterest || 0,
      })).sort((a, b) => b.value - a.value);

      const volumeList = data.map(item => ({
        strikePrice: item.strikePrice,
        value: item?.[optionType]?.TotalQtyTraded || 0,
      })).sort((a, b) => b.value - a.value);

      // Top entries
      const topOIChange = oiChangeList[0];
      const topOI = oiList[0];
      const topVolume = volumeList[0];

      // Prepare distance to spotPrice
      const distances = [topOIChange, topOI, topVolume].map(entry => ({
        ...entry,
        diff: Math.abs(entry.strikePrice - spotPrice)
      }));

      // Nearest important strike price
      const nearest = distances.sort((a, b) => a.diff - b.diff)[0];
      const currentIndex = data.findIndex(item => item.strikePrice === nearest.strikePrice);
      let shiftFrom = null;

      for (let i = 0; i < data.length; i++) {
        const item = data[i];
        if (
          item?.[optionType]?.OpenInterestChange === nearest.value ||
          item?.[optionType]?.OpenInterest === nearest.value ||
          item?.[optionType]?.TotalQtyTraded === nearest.value
        ) {
          if (i !== currentIndex) {
            shiftFrom = item.strikePrice;
            break;
          }
        }
      }

      // Store shifted strike price in both localStorage & sessionStorage
      if (shiftFrom !== null) {
        localStorage.setItem(`${optionType}_ShiftStrike`, shiftFrom);
        sessionStorage.setItem(`${optionType}_ShiftStrike`, shiftFrom);
      }

      // Decide SFB or SFT
      if (shiftFrom !== null) {
        return nearest.strikePrice > shiftFrom
          ? `SFB ${shiftFrom} → ${nearest.strikePrice}`
          : `SFT ${shiftFrom} → ${nearest.strikePrice}`;
      }
      return `No shift detected`;
    };

    // Run automatically for CE and PE
    const ceResult = detectShift('CE');
    const peResult = detectShift('PE');

    return {
      CE: ceResult,
      PE: peResult
    };
  };


  if (!isLoggedIn) {
    // Redirect to login if not logged in
    return <Navigate to="/" />;
  }
  // new code // Step 1: Unique, sorted strike prices
  const strikePrices = [...new Set(data.map(item => parseFloat(item.strikePrice)))].sort((a, b) => a - b);

  // Step 2: Identify lower & upper for nearest strikes
  let lower = null, upper = null;
  for (let i = 0; i < strikePrices.length - 1; i++) {
    if (strikePrices[i] <= spotPrice && strikePrices[i + 1] >= spotPrice) {
      lower = strikePrices[i];
      upper = strikePrices[i + 1];
      break;
    }
  }

  // Step 3: Define 2 lower and 2 upper
  const lowerStrikes = strikePrices.filter(price => price < spotPrice);
  const upperStrikes = strikePrices.filter(price => price > spotPrice);
  const twoLower = lowerStrikes.slice(-2);
  const twoUpper = upperStrikes.slice(0, 2);

  // Step 4: Initialize all totals
  let totalCE_1 = 0, totalPE_1 = 0;
  let totalCE_2 = 0, totalPE_2 = 0;
  let totalCELower = 0, totalPELower = 0;
  let totalCEUpper = 0, totalPEUpper = 0;

  // Step 5: Loop once and compute all
  strikePrices.forEach(strike => {
    const ce = data.find(item => item.CE && parseFloat(item.strikePrice) === strike)?.CE?.OpenInterestChange || 0;
    const pe = data.find(item => item.PE && parseFloat(item.strikePrice) === strike)?.PE?.OpenInterestChange || 0;

    if (strike === lower || strike === upper) {
      totalCE_1 += ce;
      totalPE_1 += pe;
    }

    if (twoLower.includes(strike) || twoUpper.includes(strike)) {
      totalCE_2 += ce;
      totalPE_2 += pe;
    }

    if (strike < spotPrice) {
      totalCELower += ce;
      totalPELower += pe;
    }

    if (strike > spotPrice) {
      totalCEUpper += ce;
      totalPEUpper += pe;
    }
  });

  const totalCE_All = totalCELower + totalCEUpper;
  const totalPE_All = totalPELower + totalPEUpper;
  const topOI = [];

  data.forEach((item) => {
    if (item.CE) {
      topOI.push({
        strikePrice: item.strikePrice,
        type: "CE",
        change: item.CE.OpenInterestChange,
      });
    }
    if (item.PE) {
      topOI.push({
        strikePrice: item.strikePrice,
        type: "PE",
        change: item.PE.OpenInterestChange,
      });
    }
  });

  // Sort by highest Open Interest Change
  const top5OI = topOI.sort((a, b) => b.change - a.change).slice(0, 5);
  // mew code 2
  // Step 1: Extract and sort unique strike prices
  const uniqueStrikePrices = [...new Set(data.map(entry => parseFloat(entry.strikePrice)))].sort((a, b) => a - b);

  // Step 2: Find 3 closest strike prices to the current spot price
  const nearbyStrikes = uniqueStrikePrices
    .map(price => ({ value: price, difference: Math.abs(price - spotPrice) }))
    .sort((a, b) => a.difference - b.difference)
    .slice(0, 3)
    .map(entry => entry.value)
    .sort((a, b) => a - b); // Optional sort for presentation

  // Step 3: Calculate Open Interest Change and Total
  let cumulativeCallOIChange = 0;
  let cumulativePutOIChange = 0;

  const analyzedStrikes = nearbyStrikes.map(strikeValue => {
    const matchedOption = data.find(opt => parseFloat(opt.strikePrice) === strikeValue);
    const callChange = matchedOption?.CE?.OpenInterestChange || 0;
    const putChange = matchedOption?.PE?.OpenInterestChange || 0;

    cumulativeCallOIChange += callChange;
    cumulativePutOIChange += putChange;

    let changeComparison = '';
    if (callChange > putChange) changeComparison = 'CALL > PUT';
    else if (putChange > callChange) changeComparison = 'PUT > CALL';
    else changeComparison = 'CALL = PUT';

    return {
      strikePrice: strikeValue,
      callOIChange: callChange,
      putOIChange: putChange,
      changeComparison
    };
  });

  // Step 4: Final OI Change Comparison Summary
  let finalOIComparison = '';
  if (cumulativeCallOIChange > cumulativePutOIChange) finalOIComparison = 'TOTAL: CW > PW';
  else if (cumulativePutOIChange > cumulativeCallOIChange) finalOIComparison = 'TOTAL: PW > CW';
  else finalOIComparison = 'TOTAL: CW = PW';

  const VegaData = [
    { greeks: 'Delta', call: currentDeltaCall, put: currentDeltaPut, diff: currentDeltaCall - currentDeltaPut },
    { greeks: 'Vega', call: currentVegaCall, put: currentVegaPut, diff: currentVegaCall - currentVegaPut },
    { greeks: 'Gamma', call: currentGammaCall, put: currentGammaPut, diff: currentGammaCall - currentGammaPut },
    { greeks: 'Theta', call: currentThetaCall, put: currentThetaPut, diff: currentThetaCall - currentThetaPut },
  ];

  // Greeks totals: sum of each Greek over every strike of the chain, Calls and Puts separately,
  // and the difference Put − Call. Recomputed on every chain update.
  const greekTotals = (() => {
    const rows = [['Delta', 'Delta'], ['Gamma', 'Gamma'], ['Theta', 'Theta'], ['Vega', 'Vega']];
    return rows.map(([label, field]) => {
      let call = 0;
      let put = 0;
      for (const item of fullChain) {
        const c = Number(item?.CE?.[field]);
        const p = Number(item?.PE?.[field]);
        if (Number.isFinite(c)) call += c;
        if (Number.isFinite(p)) put += p;
      }
      return { greeks: label, call, put, diff: put - call };
    });
  })();
  const fmtGreek = (v) => {
    const n = Number(v) || 0;
    return Math.abs(n) >= 100 ? n.toFixed(2) : Math.abs(n) >= 1 ? n.toFixed(2) : n.toFixed(4);
  };

  const getDiffColor = (diff) => {
    return diff >= 0 ? 'text-green-600' : 'text-red-600';
  };


  const renderChart = () => {
    if (activeTab !== "Delta") return <div className="text-center p-4">Coming soon...</div>;

    return (
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={sampleData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="time" />
          <YAxis yAxisId="left" domain={[4.5, 7]} />
          <YAxis yAxisId="right" orientation="right" domain={[-1, 2]} />
          <Tooltip />
          <Legend />
          <Line yAxisId="left" type="monotone" dataKey="deltaCE" stroke="green" name="Delta CE" />
          <Line yAxisId="left" type="monotone" dataKey="deltaPE" stroke="red" name="Delta PE" />
          <Line yAxisId="right" type="monotone" dataKey="deltaDiff" stroke="blue" name="Delta (CE-PE)" />
        </LineChart>
      </ResponsiveContainer>
    );
  };
  // console.log("greekChainData", greekChainData)
  console.log("data", data)
  // const futureData = [
  //   {
  //     time: "09:30AM",
  //     spotPrice: 25150,
  //     futurePrice: 25170,
  //     futureOI: 13200000
  //   },
  //   {
  //     time: "09:45AM",
  //     spotPrice: 25140,
  //     futurePrice: 25160,
  //     futureOI: 13220000
  //   },
  //   {
  //     time: "10:00AM",
  //     spotPrice: 25130,
  //     futurePrice: 25150,
  //     futureOI: 13250000
  //   },
  //   {
  //     time: "10:15AM",
  //     spotPrice: 25135,
  //     futurePrice: 25155,
  //     futureOI: 13270000
  //   },
  //   {
  //     time: "10:30AM",
  //     spotPrice: 25120,
  //     futurePrice: 25145,
  //     futureOI: 13280000
  //   },
  //   {
  //     time: "10:45AM",
  //     spotPrice: 25125,
  //     futurePrice: 25140,
  //     futureOI: 13260000
  //   },
  //   {
  //     time: "11:00AM",
  //     spotPrice: 25110,
  //     futurePrice: 25130,
  //     futureOI: 13230000
  //   },
  //   {
  //     time: "11:15AM",
  //     spotPrice: 25105,
  //     futurePrice: 25120,
  //     futureOI: 13210000
  //   },
  //   {
  //     time: "11:30AM",
  //     spotPrice: 25100,
  //     futurePrice: 25115,
  //     futureOI: 13240000
  //   },
  //   {
  //     time: "11:45AM",
  //     spotPrice: 25095,
  //     futurePrice: 25110,
  //     futureOI: 13260000
  //   }
  // ];

  return (
    <div>
      <section className="lot-size-section oc-table-section">
        <div>
          {(localStorage.getItem("stock") === "NIFTY" || localStorage.getItem("stock") === "SENSEX") && (
            <strong style={{ color: 'blue', fontWeight: 'bold', display: 'block', fontSize: '20px', textAlign: 'center' }}>
              WEEKLY RANGE: {weeklyUpper.toFixed(2)} ↕️ {weeklyDown.toFixed(2)}
            </strong>
          )}
        </div><br />
        <div>
          <button x
            class="btn btn-primary mb-2"
            data-bs-toggle="modal"
            data-bs-target="#callVsPutOI"
            style={{
              backgroundColor: '#f77c22',
              border: '2px solid #f77c22',
              borderRadius: '10px',
              padding: '5px 15px',
              fontWeight: 'bold',
              fontSize: '14px',
              marginBottom: "10px"
            }}
          >
            Call Vs Put OI
          </button>&nbsp;
          <div style={styles.dropdown}>
            <button style={styles.dropbtn} onClick={toggleDropdown}>
              GREEKS ⮟
            </button>
            {open && (
              <div style={styles.dropdownContent}>
                {greekitems.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      ...styles.dropdownItem,
                      ...(index === greekitems.length - 1 ? styles.lastItem : {}),
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#e46c15'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#f77c22'}
                    onClick={() => handleGreekSelect(item)} // use this
                  >
                    {item}
                  </div>
                ))}
              </div>
            )}
            {/* <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}> */}
            &nbsp; <button
              onClick={() => toggleButton('IV')}
              style={{
                backgroundColor: activeButtons.includes('IV') ? '#f77c22' : '#fff9e7',
                border: '2px solid #f77c22',
                color: activeButtons.includes('IV') ? 'white' : '#f77c22',
                borderRadius: '10px',
                padding: '5px 15px',
                fontWeight: 'bold',
                fontSize: '14px',
              }}
            >
              IV
            </button>

            &nbsp;<button
              onClick={() => toggleButton('IV/&/Y')}
              style={{
                backgroundColor: activeButtons.includes('IV/&/Y') ? '#f77c22' : '#fff9e7',
                border: '2px solid #f77c22',
                color: activeButtons.includes('IV/&/Y') ? 'white' : '#f77c22',
                borderRadius: '10px',
                padding: '5px 10px',
                fontWeight: 'bold',
                fontSize: '14px',
              }}
            >
              I.V./&/Y
            </button>&nbsp;
            <button
              class="btn btn-primary mb-2"
              data-bs-toggle="modal"
              data-bs-target="#top5openinterestchnage"
              style={{
                backgroundColor: '#f77c22',
                border: '2px solid #f77c22',
                borderRadius: '10px',
                padding: '5px 15px',
                fontWeight: 'bold',
                fontSize: '14px',
                marginBottom: "10px"
              }}
            >
              Top 5 OpenInterestChange
            </button>
            &nbsp;
            <button
              className="btn btn-primary mb-2"
              onClick={() => {
                setShowTable(!showTable);
                toggleButton('New Feature');
              }}
              style={{
                backgroundColor: activeButtons.includes('New Feature') ? '#f77c22' : '#fff9e7',
                border: '2px solid #f77c22',
                color: activeButtons.includes('New Feature') ? 'white' : '#f77c22',
                borderRadius: '10px',
                padding: '5px 15px',
                fontWeight: 'bold',
                fontSize: '14px',
              }}
            >
              New Feature
            </button>

            &nbsp;
            {/* {(localStorage.getItem("stock") === "NIFTY" || localStorage.getItem("stock") === "BANKNIFTY") && ( */}
            <button
              className="btn btn-primary mb-2"
              data-bs-toggle="modal"
              data-bs-target="#callAndPutWriting"
              style={{
                backgroundColor: '#f77c22',
                border: '2px solid #f77c22',
                borderRadius: '10px',
                padding: '5px 15px',
                fontWeight: 'bold',
                fontSize: '14px',
              }}
            >
              Call Writing And Put Writing
            </button>
            <div style={{ ...styles.dropdown, display: 'inline-block', position: 'relative', marginLeft: '10px' }}>
              <button style={styles.dropbtn} onClick={toggleStrikeDropdown}>
                Select Strike ⮟
              </button>
              {strikeDropdownOpen && (
                <div style={styles.dropdownContent}>
                  {STRIKE_OPTIONS.map((option, index) => (
                    <div
                      key={index}
                      style={{
                        ...styles.dropdownItem,
                        ...(index === STRIKE_OPTIONS.length - 1 ? styles.lastItem : {})
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#e46c15'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#f77c22'}
                      onClick={() => handleStrikeSelect(option)}
                    >
                      {option}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Spot Chart popup (moved here from the top header) */}
            <button
              type="button"
              className="btn btn-primary mb-2"
              title="Spot chart with support / resistance"
              data-bs-toggle="modal"
              data-bs-target={`#${SPOT_CHART_MODAL_ID}`}
              style={{
                backgroundColor: '#f77c22',
                border: '2px solid #f77c22',
                borderRadius: '10px',
                padding: '5px 15px',
                fontWeight: 'bold',
                fontSize: '14px',
                marginLeft: '10px',
              }}
            >
              <i className="fa fa-line-chart me-2" />
              Chart
            </button>

            {/* </div> */}
          </div>

        </div>
        <div className="container-fluid px-0">
          <div className="lot-size-inner otw-sticky-box" ref={tableBoxRef} style={{ overflowX: "auto" }}>
            <table className="table-left">
              {loading ? (
                <div className="loading-indicator">
                  <img src="/img/Spinner-2.gif" alt="Loading..." />
                </div> // Display loading indicator when loading is true
              ) : (
                <tbody>
                  {/* <tr className="table-first">
                    <th>TvItm:{countUperHeader(data).itmTv}</th>
                    <th>LTPChg:{countUperHeader(data).ltpChg}</th>
                    <th>OIChg:{countUperHeader(data).oichagPer}%</th>
                    <th colSpan={2}>(CE)R : 21300</th>
                    {showSwing && (
                      <th></th>
                    )}
                    <th className="">{date}</th>
                    {showSwing && (
                      <th className="table-right"></th>
                    )}

                    <th className="table-right">(PE)R : 21300</th>
                    <th className="table-right">OIChg:{countUperHeader(data).oichagPePer}%</th>
                    <th className="table-right">LTPChg:{countUperHeader(data).ltpChgPE}</th>
                    <th colSpan={2} className="table-right">
                      TvItm:{countUperHeader(data).itmTvPE}
                    </th>
                  </tr> */}
                  {/* Top header row: one cell over the call columns, the date over the strike column,
                      one cell over the put columns — spans follow the columns shown. */}
                  <tr ref={headerRow1Ref} className="tableResistance otw-sticky-row1" style={{ backgroundColor: 'red', color: 'white' }}>
                    <th colSpan={sideColumns}>
                      <div className="bt-header-stats">
                        <span>TvItm:{countUperHeader(data).itmTv}</span>
                        <span>LTPChg:{countUperHeader(data).ltpChg}</span>
                        <span>OIChg:{countUperHeader(data).oichagPer}%</span>
                        <span>(CE)R : 21300</span>
                      </div>
                    </th>
                    <th className="strike-price" style={{ backgroundColor: 'black', color: 'white' }}>{date}</th>
                    <th colSpan={sideColumns} style={{ backgroundColor: 'green', color: 'white' }}>
                      <div className="bt-header-stats">
                        <span>(PE)R : 21300</span>
                        <span>OIChg:{countUperHeader(data).oichagPePer}%</span>
                        <span>LTPChg:{countUperHeader(data).ltpChgPE}</span>
                        <span>TvItm:{countUperHeader(data).itmTvPE}</span>
                      </div>
                    </th>
                  </tr>

                  <tr style={{ backgroundColor: 'red', color: 'white' }}>
                    {selectedGreeks.includes('GAMMA') && <th>Gamma</th>}
                    {selectedGreeks.includes('VEGA') && <th>Vega</th>}
                    {selectedGreeks.includes('THETA') && <th>Theta</th>}
                    {selectedGreeks.includes('DELTA') && <th>Delta</th>}
                    {activeButtons.includes('IV') && <th>IV</th>}
                    {activeButtons.includes('IV/&/Y') && <th>IV/&amp;/y</th>}
                    {/* <th>IV/&amp;/y</th> */}
                    <th>OI Chg</th>
                    <th>OI</th>
                    <th>Volume</th>
                    <th>LTP/Chg(TV)</th>
                    {!showSwing && (
                      <th></th>
                    )}
                    {showSwing && (
                      <th style={{ color: '#fff', backgroundColor: '#ff505c' }}>Probable <br />Swing High</th>
                    )}

                    <th className="strike-price" style={{ color: '#000' }}>SP:PCR Oi/OiC</th>
                    {!showSwing && (
                      <th></th>
                    )}
                    {showSwing && (
                      <th style={{ color: '#fff', backgroundColor: '#2bba2b' }} className="table-right">Probable <br />Swing Low</th>
                    )}
                    <th className="table-right" style={{ backgroundColor: 'green', color: 'white' }}>LTP/Chg(TV)</th>
                    <th className="table-right" style={{ backgroundColor: 'green', color: 'white' }}>Volume</th>
                    <th className="table-right" style={{ backgroundColor: 'green', color: 'white' }}>OI</th>
                    <th className="table-right" style={{ backgroundColor: 'green', color: 'white' }}>OI Chg</th>
                    {/* <th className="table-right">IV/&amp;/y</th> */}
                    {activeButtons.includes('IV/&/Y') && <th className="table-right" style={{ backgroundColor: 'green', color: 'white' }}>IV/&amp;/y</th>}
                    {activeButtons.includes('IV') && <th className="table-right" style={{ backgroundColor: 'green', color: 'white' }}>IV</th>}
                    {selectedGreeks.includes('GAMMA') && <th className="table-right" style={{ backgroundColor: 'green', color: 'white' }}>Gamma</th>}
                    {selectedGreeks.includes('VEGA') && <th className="table-right" style={{ backgroundColor: 'green', color: 'white' }}>Vega</th>}
                    {selectedGreeks.includes('THETA') && <th className="table-right" style={{ backgroundColor: 'green', color: 'white' }}>Theta</th>}
                    {selectedGreeks.includes('DELTA') && <th className="table-right" style={{ backgroundColor: 'green', color: 'white' }}>Delta</th>}
                  </tr>

                  {Array.isArray(data) &&
                    data.map((item, index) => {
                      localStorage.getItem('reverseTable') === 'true' ? isSpotPrice =
                        spotPrice &&
                        item.strikePrice <= spotPrice &&
                        (!data[index - 1] ||
                          spotPrice < data[index - 1].strikePrice) : isSpotPrice =
                      spotPrice &&
                      item.strikePrice >= spotPrice &&
                      (!data[index - 1] ||
                        spotPrice > data[index - 1].strikePrice);
                      return (
                        <React.Fragment key={index}
                        >
                          {isSpotPrice && getSpotPriceRow()}
                          <tr
                            // style={{
                            //   backgroundColor: isSpotPrice
                            //     ? "#d1e7dd"
                            //     : "transparent",
                            // }}
                            style={{
                              ...(isSpotPrice && { backgroundColor: "#d1e7dd" }),
                              ...(item.strikePrice.toString().endsWith("00") && { backgroundColor: "#ffffae" }),
                            }}

                          >
                            {selectedGreeks.includes('GAMMA') && (
                              <td>
                                {parseFloat(item.CE?.Gamma ?? 0).toFixed(4)}
                              </td>
                            )}
                            {selectedGreeks.includes('VEGA') && (
                              <td>
                                {parseFloat(item.CE?.Vega ?? 0).toFixed(2)}
                              </td>
                            )}

                            {selectedGreeks.includes('THETA') && (
                              <td>
                                {parseFloat(item.CE?.Theta ?? 0).toFixed(2)}
                              </td>
                            )}

                            {selectedGreeks.includes('DELTA') && (
                              <td>
                                {parseFloat(item.CE?.Delta ?? 0).toFixed(2)}
                              </td>
                            )}
                            {activeButtons.includes('IV') && <td>{formatIV(item.CE?.IV)}</td>}
                            {activeButtons.includes('IV/&/Y') &&
                              <td>
                                <button>
                                  {formatIV(item.CE?.IV)}
                                  <br />
                                  {parseFloat(item.CE?.Gamma ?? 0).toFixed(2)}/
                                  {parseFloat(item.CE?.Theta ?? 0).toFixed(2)}
                                </button>
                              </td>
                            }
                            <td>
                              <button
                                style={{
                                  backgroundColor:
                                    calculatePercentage(item.CE?.OpenInterestChange ?? 0, highestCEOIChange) < 75
                                      ? "inherit"
                                      : item.CE?.OpenInterestChange === highestCEOIChange
                                        ? "#f55366" // Red for the highest
                                        : item.CE?.OpenInterestChange === secondHighestCEOIChange && calculatePercentage(item.CE?.OpenInterestChange ?? 0, highestCEOIChange) > 70
                                          ? "#E2CF64" // Yellow for second highest if percentage > 70
                                          : item.CE?.OpenInterestChange === thirdHighestCEOIChange && calculatePercentage(item.CE?.OpenInterestChange ?? 0, highestCEOIChange) > 70 && localStorage.getItem('highThirdHighest') === 'true'
                                            ? "#E5E4C2" // Light Yellow for third highest if percentage > 70
                                            : "inherit", // Default background if no match,
                                  color:
                                    item.CE?.OpenInterestChange === highestCEOIChange && highestCEOIChange > 0
                                      ? "#fff" // White text if background is red
                                      : "inherit",
                                }}
                                data-bs-toggle="modal"
                                data-bs-target="#oichartModel"
                                onClick={() =>
                                  onHistoricsMultipleClick(item.CE)
                                }
                              >
                                <div>
                                  {localStorage.getItem('preferPercentage') === 'true' ? calculatePercentage(
                                    item.CE?.OpenInterestChange ?? 0,
                                    highestCEOIChange
                                  ) : formatNumber(
                                    item.CE?.OpenInterestChange ?? 0
                                  )}
                                  {localStorage.getItem('preferPercentage') === 'true' ? '%' : ''}

                                </div>
                                <div>
                                  {localStorage.getItem('preferPercentage') === 'true' ? formatNumber(
                                    item.CE?.OpenInterestChange ?? 0
                                  ) : calculatePercentage(
                                    item.CE?.OpenInterestChange ?? 0,
                                    highestCEOIChange
                                  )}
                                  {localStorage.getItem('preferPercentage') === 'true' ? '' : "%"}
                                </div>
                              </button>
                            </td>
                            <td>
                              <button
                                style={{
                                  backgroundColor:
                                    calculatePercentage(item.CE?.OpenInterest ?? 0, highestCEOI) < 75
                                      ? "inherit"
                                      : item.CE?.OpenInterest === highestCEOI
                                        ? "#f55366" // Red for the highest
                                        : item.CE?.OpenInterest === secondHighestCEOI &&
                                          calculatePercentage(item.CE?.OpenInterest ?? 0, highestCEOI) > 70
                                          ? "#E2CF64" // Yellow for second highest if percentage > 70
                                          : item.CE?.OpenInterest === thirdHighestCEOI &&
                                            calculatePercentage(item.CE?.OpenInterest ?? 0, highestCEOI) > 70 && localStorage.getItem('highThirdHighest') === 'true'
                                            ? "#E5E4C2" // Light Yellow for third highest if percentage > 70
                                            : "inherit", // Default background if no match,
                                  color:
                                    item.CE?.OpenInterest === highestCEOI && highestCEOI > 0
                                      ? "#fff" // White text for red background
                                      : "inherit",

                                }}
                                data-bs-toggle="modal"
                                data-bs-target="#oipercentage1Model"
                                onClick={() => onHistoricsClick(item.CE)}
                              >
                                <div>
                                  {localStorage.getItem('preferPercentage') === 'true' ? calculatePercentage(
                                    item.CE?.OpenInterest ?? 0,
                                    highestCEOI
                                  ) : formatNumber(item.CE?.OpenInterest ?? 0)}
                                  {localStorage.getItem('preferPercentage') === 'true' ? '%' : ''}
                                </div>
                                <div>
                                  {localStorage.getItem('preferPercentage') === 'true' ? formatNumber(item.CE?.OpenInterest ?? 0) :
                                    calculatePercentage(
                                      item.CE?.OpenInterest ?? 0,
                                      highestCEOI
                                    )}
                                  {localStorage.getItem('preferPercentage') === 'true' ? '' : "%"}
                                </div>
                              </button>
                            </td>
                            <td>
                              <button
                                style={{
                                  backgroundColor: calculatePercentage(item.CE?.TotalQtyTraded ?? 0, highestCEVolume) < 75
                                    ? "inherit"
                                    : item.CE?.TotalQtyTraded === highestCEVolume
                                      ? "#f55366" // Red for the highest
                                      : item.CE?.TotalQtyTraded === secondHighestCEVolume &&
                                        calculatePercentage(item.CE?.TotalQtyTraded ?? 0, highestCEVolume) > 70
                                        ? "#E2CF64" // Yellow for second highest if percentage > 70
                                        : item.CE?.TotalQtyTraded === thirdHighestCEVolume &&
                                          calculatePercentage(item.CE?.TotalQtyTraded ?? 0, highestCEVolume) > 70 && localStorage.getItem('highThirdHighest') === 'true'
                                          ? "#E5E4C2" // Light Yellow for third highest if percentage > 70
                                          : "inherit", // Default background if no match,

                                  color:
                                    item.CE?.TotalQtyTraded === highestCEVolume && highestCEVolume > 0
                                      ? "#fff" // White text if red background
                                      : "inherit",

                                }}
                                data-bs-toggle="modal"
                                data-bs-target="#volumeChartModel"
                                onClick={() =>
                                  setSelectedStrikePrice(item.strikePrice)
                                }
                              >
                                <div>
                                  {localStorage.getItem('preferPercentage') === 'true' ? calculatePercentage(
                                    item.CE?.TotalQtyTraded ?? 0,
                                    highestCEVolume
                                  ) : formatNumber(item.CE?.TotalQtyTraded ?? 0)}

                                  {localStorage.getItem('preferPercentage') === 'true' ? '%' : ''}
                                </div>
                                <div>
                                  {localStorage.getItem('preferPercentage') === 'true' ? formatNumber(item.CE?.TotalQtyTraded ?? 0) : calculatePercentage(item.CE?.TotalQtyTraded ?? 0, highestCEVolume)}
                                  {localStorage.getItem('preferPercentage') === 'true' ? '' : "%"}
                                </div>
                              </button>
                            </td>
                            <td>
                              <button
                                data-bs-toggle="modal"
                                data-bs-target="#LPTcalculatorModel"
                              >
                                {item.CE?.LastTradePrice ?? 0}<br />{item?.CE?.PriceChange}/({ivCalculate(item)})
                              </button>
                            </td>
                            {showSwing && (
                              <td style={{ color: '#fff', backgroundColor: '#ff505c' }} >
                                <button
                                  data-bs-toggle="modal"
                                  data-bs-target="#LPTcalculatorModel"
                                  style={{
                                    color: '#fff',
                                    border: 'none',
                                    padding: '5px 10px',
                                    cursor: 'pointer',
                                  }}
                                >
                                  {countResistansAndSupport(item, index)}
                                  <div>{countResistans(item, index).resistance.toFixed(2)}</div>
                                </button>
                              </td>
                            )}
                            {!showSwing && (
                              <td></td>
                            )}
                            <td
                              className={`strike-price ${getBackgroundClass(item)}`}
                              style={Number(item.strikePrice) === spotStrike ? { backgroundColor: '#b3261e', color: '#fff' } : undefined}
                            >
                              <button
                                data-bs-toggle="modal"
                                data-bs-target="#strikePriceModel"
                                onClick={() => onStrikePriceClick(item, index)}
                                style={Number(item.strikePrice) === spotStrike ? { color: '#fff', fontWeight: 'bold' } : undefined}
                              >
                                {item.strikePrice}
                                <br />
                                {countPcrOiBStrike(item)}/{countPcrOiChgBStrike(item)}
                              </button>
                            </td>
                            {!showSwing && (
                              <td></td>
                            )}
                            {showSwing && (
                              <td className="table-right" style={{
                                backgroundColor: "#2bba2b", color: '#fff',
                              }}>
                                <button
                                  data-bs-toggle="modal"
                                  data-bs-target="#CombinedpriceModel"
                                  style={{
                                    color: '#fff',
                                    border: 'none',
                                    padding: '5px 10px',
                                    cursor: 'pointer',
                                  }}
                                >
                                  {countSupportAndResistance(item, index)}
                                  {countResistans(item, index).support.toFixed(2)}
                                </button>
                              </td>
                            )}
                            <td className="table-right" style={{
                              ...(item.strikePrice.toString().endsWith("00") && { backgroundColor: "#ffffae" }),
                            }}>
                              <button
                                data-bs-toggle="modal"
                                data-bs-target="#CombinedpriceModel"
                              >
                                {item.PE?.LastTradePrice ?? 0} <br />{item?.PE?.PriceChange}/({ivCalculatePE(item)})
                              </button>
                            </td>
                            <td className="table-right" style={{
                              ...(item.strikePrice.toString().endsWith("00") && { backgroundColor: "#ffffae" }),
                            }}>
                              <button
                                style={{
                                  backgroundColor: calculatePercentage(item.PE?.TotalQtyTraded ?? 0, highestPEVolume) < 75
                                    ? "inherit"
                                    : item.PE?.TotalQtyTraded === highestPEVolume
                                      ? "green"
                                      : item.PE?.TotalQtyTraded ===
                                        secondHighestPEVolume && calculatePercentage(
                                          item.PE?.TotalQtyTraded ?? 0,
                                          highestPEVolume
                                        ) > 70
                                        ? "#E2CF64"
                                        : item.PE?.TotalQtyTraded ===
                                          thirdHighestPEVolume && calculatePercentage(
                                            item.PE?.TotalQtyTraded ?? 0,
                                            highestPEVolume
                                          ) > 70 && localStorage.getItem('highThirdHighest') === 'true'
                                          ? "#E5E4C2"
                                          : "inherit",
                                  color:
                                    item.PE?.TotalQtyTraded === highestPEVolume && highestPEVolume > 0
                                      ? "#fff" // White text when background is green
                                      : "inherit",

                                }}
                                data-bs-toggle="modal"
                                data-bs-target="#volumeChartModel12"
                                onClick={() =>
                                  setSelectedStrikePrice(item.strikePrice)
                                }
                              >
                                <div>
                                  {localStorage.getItem('preferPercentage') === 'true' ? calculatePercentage(
                                    item.PE?.TotalQtyTraded ?? 0,
                                    highestPEVolume
                                  ) : formatNumber(item.PE?.TotalQtyTraded ?? 0)}
                                  {localStorage.getItem('preferPercentage') === 'true' ? '%' : ''}
                                </div>
                                <div>
                                  {localStorage.getItem('perferPercentage') === 'true' ? formatNumber(item.PE?.TotalQtyTraded ?? 0) : calculatePercentage(item.PE?.TotalQtyTraded ?? 0, highestPEVolume)}
                                  {localStorage.getItem('preferPercentage') === 'true' ? '' : "%"}
                                </div>
                              </button>
                            </td>
                            <td className="table-right" style={{
                              ...(item.strikePrice.toString().endsWith("00") && { backgroundColor: "#ffffae" }),
                            }}>
                              <button
                                style={{
                                  backgroundColor: calculatePercentage(item.PE?.OpenInterest ?? 0, highestPEOI) < 75
                                    ? "inherit"
                                    :
                                    item.PE?.OpenInterest === highestPEOI
                                      ? "green"
                                      : item.PE?.OpenInterest ===
                                        secondHighestPEOI && calculatePercentage(
                                          item.PE?.OpenInterest ?? 0,
                                          highestPEOI
                                        ) > 70
                                        ? "#E2CF64"
                                        : item.PE?.OpenInterest ===
                                          thirdHighestPEOI && calculatePercentage(
                                            item.PE?.OpenInterest ?? 0,
                                            highestPEOI
                                          ) > 70 && localStorage.getItem('highThirdHighest') === 'true'
                                          ? "#E5E4C2"
                                          : "inherit",
                                  color:
                                    item.PE?.OpenInterest === highestPEOI && highestPEOI > 0
                                      ? "#fff" // White text for green background
                                      : "inherit",

                                }}
                                data-bs-toggle="modal"
                                data-bs-target="#oipercentage1Model"
                                onClick={() => onHistoricsClick(item.PE)}
                              >
                                <div>
                                  {
                                    localStorage.getItem('preferPercentage') === 'true' ? calculatePercentage(
                                      item.PE?.OpenInterest ?? 0,
                                      highestPEOI
                                    ) : formatNumber(item.PE?.OpenInterest ?? 0)
                                  }
                                  {localStorage.getItem('preferPercentage') === 'true' ? '%' : ''}
                                </div>
                                <div>
                                  {localStorage.getItem('preferPercentage') === 'true' ? formatNumber(item.PE?.OpenInterest ?? 0) : calculatePercentage(
                                    item.PE?.OpenInterest ?? 0,
                                    highestPEOI
                                  )}

                                  {localStorage.getItem('preferPercentage') === 'true' ? '' : "%"}
                                </div>
                              </button>
                            </td>
                            <td className="table-right" style={{
                              ...(item.strikePrice.toString().endsWith("00") && { backgroundColor: "#ffffae" }),
                            }}>
                              <button
                                style={{
                                  backgroundColor: calculatePercentage(item.PE?.OpenInterestChange ?? 0, highestPEOIChange) < 75
                                    ? "inherit"
                                    :
                                    item.PE?.OpenInterestChange ===
                                      highestPEOIChange
                                      ? "green"
                                      : item.PE?.OpenInterestChange ===
                                        secondHighestPEOIChange && calculatePercentage(
                                          item.PE?.OpenInterestChange ?? 0,
                                          highestPEOIChange
                                        ) > 70
                                        ? "#E2CF64"
                                        : item.PE?.OpenInterestChange ===
                                          thirdHighestPEOIChange && calculatePercentage(
                                            item.PE?.OpenInterestChange ?? 0,
                                            highestPEOIChange
                                          ) > 70 && localStorage.getItem('highThirdHighest') === 'true'
                                          ? "#E5E4C2"
                                          : "inherit",
                                  color:
                                    item.PE?.OpenInterestChange === highestPEOIChange && highestPEOIChange > 0
                                      ? "#fff" // White text if background is green
                                      : "inherit",

                                }}
                                data-bs-toggle="modal"
                                data-bs-target="#oichartModel"
                                onClick={() =>
                                  onHistoricsMultipleClick(item.PE)
                                }
                              >
                                <div>
                                  {
                                    localStorage.getItem('preferPercentage') === 'true' ? calculatePercentage(item.PE?.OpenInterestChange ?? 0, highestPEOIChange) : formatNumber(item.PE?.OpenInterestChange ?? 0)
                                  }

                                  {localStorage.getItem('preferPercentage') === 'true' ? '%' : ''}
                                </div>
                                <div>
                                  {
                                    localStorage.getItem('preferPercentage') === 'true' ? formatNumber(item.PE?.OpenInterestChange ?? 0) : calculatePercentage(item.PE?.OpenInterestChange ?? 0, highestPEOIChange)
                                  }
                                  {
                                    localStorage.getItem('preferPercentage') === 'true' ? '' : '%'
                                  }
                                </div>
                              </button>
                            </td>
                            {activeButtons.includes('IV/&/Y') &&
                              <td className="table-right" style={{
                                ...(item.strikePrice.toString().endsWith("00") && { backgroundColor: "#ffffae" }),
                              }}>
                                <button
                                  data-bs-toggle="modal"
                                  data-bs-target="#LPTcalculatorModel"
                                >
                                  {formatIV(item.PE?.IV)}
                                  <br />
                                  {parseFloat(item.PE?.Gamma ?? 0).toFixed(2)}/
                                  {parseFloat(item.PE?.Theta ?? 0).toFixed(2)}
                                </button>
                              </td>
                            }
                            {activeButtons.includes('IV') && <td className="table-right" style={
                              item.strikePrice.toString().endsWith("00")
                                ? { backgroundColor: "#ffffae" }
                                : {}
                            }  >{formatIV(item.PE?.IV)}</td>}
                            {selectedGreeks.includes('GAMMA') && (
                              <td className="table-right"
                                style={
                                  item.strikePrice.toString().endsWith("00")
                                    ? { backgroundColor: "#ffffae" }
                                    : {}
                                }>
                                {parseFloat(item.PE?.Gamma ?? 0).toFixed(4)}
                              </td>
                            )}
                            {selectedGreeks.includes('VEGA') && (
                              <td className="table-right"
                                style={
                                  item.strikePrice.toString().endsWith("00")
                                    ? { backgroundColor: "#ffffae" }
                                    : {}
                                }
                              >
                                {parseFloat(item.PE?.Vega ?? 0).toFixed(2)}
                              </td>
                            )}

                            {selectedGreeks.includes('THETA') && (
                              <td className="table-right"
                                style={
                                  item.strikePrice.toString().endsWith("00")
                                    ? { backgroundColor: "#ffffae" }
                                    : {}
                                }
                              >
                                {parseFloat(item.PE?.Theta ?? 0).toFixed(2)}
                              </td>
                            )}

                            {selectedGreeks.includes('DELTA') && (
                              <td className="table-right"
                                style={
                                  item.strikePrice.toString().endsWith("00")
                                    ? { backgroundColor: "#ffffae" }
                                    : {}
                                }>
                                {parseFloat(item.PE?.Delta ?? 0).toFixed(2)}
                              </td>
                            )}
                          </tr>
                        </React.Fragment>
                      );
                    })}
                </tbody>
              )}
            </table>
          </div>
        </div>
      </section>
      <section className="volume-section ">
        <div className="container">
          <div className="volume-section-inner">
            {/* <ul className="list-unstyled d-flex align-items-center justify-content-center">
              <li>
                <a href="#">TV PE: 50%</a>
              </li>
              <li>
                {" "}
                <h3>
                  Delta PE: 3.70 <br />
                  LTP PE: 31%
                </h3>
              </li>
              <li>
                {" "}
                <h3>
                  Delta PE: 3.70 <br />
                  LTP PE: 31%
                </h3>
              </li>
              <li>
                <a href="#" className="text-danger">
                  TV PE: 50%
                </a>
              </li>
            </ul> */}
          </div>

          {/*  new ai feature*/}

          {showTable && (
            <div
              id="newfeatureModel-22"
              data-bs-toggle="modal"
              data-bs-target="#newfeatureModel"
              onClick={() => setShowGraph(true)}
              className="new-feature-model max-w-sm mx-auto rounded-xl shadow  cursor-pointer hover:shadow-lg transition duration-300"
            >
              <div className="greeks-total-card">
                <div className="greeks-total-head">
                  <span>Greeks — total of all strikes</span>
                  <small>{fullChain.length} strikes</small>
                </div>
                <table className="greeks-total-table">
                  <thead>
                    <tr>
                      <th>Greek</th>
                      <th className="gt-call">Call</th>
                      <th className="gt-put">Put</th>
                      <th>Put − Call</th>
                    </tr>
                  </thead>
                  <tbody>
                    {greekTotals.map((row) => (
                      <tr key={row.greeks}>
                        <td className="gt-name">{row.greeks}</td>
                        <td>{fmtGreek(row.call)}</td>
                        <td>{fmtGreek(row.put)}</td>
                        <td className={row.diff >= 0 ? 'gt-pos' : 'gt-neg'}>
                          {row.diff > 0 ? '+' : ''}{fmtGreek(row.diff)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <div
            className="modal fade"
            id="newfeatureModel"
            tabIndex={-1}
            aria-labelledby="newfeatureModelLabel"
            aria-hidden="true"
          >
            <div className="modal-dialog ai-chart modal-dialog-centered">
              <div className="modal-content modal-bg-light-blue">
                <div className="modal-header">
                  <h5 className="modal-title" id="newfeatureModelLabel">
                    Greeks Graph
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  />
                </div>

                <div className="modal-body">
                  <GreeksGraph symbol={localStorage.getItem("stock") || "NIFTY"} />
                </div>
              </div>
            </div>
          </div>

          {/* end feature */}
          <div className="crr-volume-inner">
            <div className="row justify-content-center">
              <div className="col-md-4">
                <div className="crr-volume-content">
                  <h3>CPR Volume: {countCprVolume(data).pcrOi}</h3>
                  <p>Total Volume CE:{formatNumber2(countCprVolume(data).totalCeOI)}</p>
                  <p>Total Volume PE:{formatNumber2(countCprVolume(data).totalPeOI)}</p>

                </div>
              </div>
              <div className="col-md-4">
                <div className="crr-volume-content">
                  <h3>PCR OI: {countPcrOI(data).pcrOi}</h3>
                  <p>Total OI CE: {formatNumber2(countPcrOI(data).totalCeOI)}</p>
                  <p>Total OI PE:{formatNumber2(countPcrOI(data).totalPeOI)}</p>
                </div>
              </div>
              <div className="col-md-4">
                <div className="crr-volume-content">
                  <h3>PCR OI Change:{countPcrOIchg(data).pcrOi}</h3>
                  <p>Total OI change CE: {formatNumber2(countPcrOIchg(data).totalCeOI)}</p>
                  <p>Total OI change PE: {formatNumber2(countPcrOIchg(data).totalPeOI)}</p>
                </div>

              </div>
              {/* <div className="col-md-4">
                <div className="crr-volume-content">
                  <h4 style={{
                    color: totalPE_1 > totalCE_1
                      ? "green"
                      : totalCE_1 > totalPE_1
                        ? "red"
                        : "black"
                  }}>
                    Nearest Lower & Upper Strike spot price (COI): {totalPE_1 > totalCE_1
                      ? "PW > CW"
                      : totalCE_1 > totalPE_1
                        ? "CW > PW"
                        : "PW and CE = CW"}
                  </h4>


                </div>
              </div>
              <div className="col-md-4">
                <div className="crr-volume-content">
                  <h4 style={{
                    color: totalPE_2 > totalCE_2
                      ? "green"
                      : totalCE_2 > totalPE_2
                        ? "red"
                        : "black"
                  }}>
                    2 Lower & 2 Upper Strike spot price (COI): {totalPE_2 > totalCE_2
                      ? "PW > CW"
                      : totalCE_2 > totalPE_2
                        ? "CW > PW"
                        : "PW and CE = CW"}
                  </h4>

                </div>
              </div>
              <div className="col-md-4">
                <div className="crr-volume-content">
                  <h4 style={{
                    color: totalPE_All > totalCE_All
                      ? "green"
                      : totalCE_All > totalPE_All
                        ? "red"
                        : "black"
                  }}>
                    All Lower & Upper Strikes spot price (COI): {totalPE_All > totalCE_All
                      ? "PW > CW"
                      : totalCE_All > totalPE_All
                        ? "CW > PW"
                        : "PW and CE = CW"}
                  </h4>

                </div>
              </div> */}

            </div>
          </div>
        </div>
      </section>
      <section className="des-section">
        <div className="container">
          <div className="dessection-inner">
            <p>
              PCR is the ratio of the PUT OI divided by CALL OI. PCR is used by
              traders to gauge the overall sentiment of the market. If the PCR
              is high, it means that the market is bullish and if the PCR is
              low, it means that the market is bearish . PCR between 0.8 and 1.2
              is considered normal. If the PCR is below 0.8, it means that the
              market is bearish and if the PCR is above 1.2, it means that the
              market is bullish. PCR of OI and OI Change is directly
              proportional to CPR of volume.
            </p>
          </div>
        </div>
      </section>

      {/* ======== Setting popup ======== */}
      <div
        className="modal fade"
        id="settingModel"
        tabIndex={-1}
        aria-labelledby="settingModelLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-header">
              <h5 className="modal-title" id="settingModelLabel">
                Setting
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              <div className="setting-functionalty-outer popup-list">
                <ul className="list-unstyled">
                  <li>
                    <div className="popup-list-title">
                      <span>
                        <img src="img/light-mode.svg" alt="icon" />
                      </span>
                      <span>Light Mode</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input light-mode"
                          type="checkbox"
                          id="flexSwitchCheckChecked"
                          defaultChecked
                        />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span>
                        <img src="img/highlight.svg" alt="icon" />
                      </span>
                      <span>ITM &amp; OTM Highlight</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input itm-otm-highlight"
                          type="checkbox"
                          id="flexSwitchCheckChecked"
                          defaultChecked
                        />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span>
                        <img src="img/screen-desktop.svg" alt="icon" />
                      </span>
                      <span>Keep Screen Awake</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input keep-screen-awake"
                          type="checkbox"
                          id="flexSwitchCheckChecked"
                          defaultChecked
                        />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span>
                        <img src="img/price-tag.svg" alt="icon" />
                      </span>
                      <span>Show Future Price</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input show-future-price"
                          type="checkbox"
                          id="flexSwitchCheckChecked"
                          defaultChecked
                        />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span>
                        <img src="img/top-so.svg" alt="icon" />
                      </span>
                      <span>High Third Highest</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input high-third-highest"
                          type="checkbox"
                          id="flexSwitchCheckChecked"
                          defaultChecked
                        />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span>
                        <img src="img/number-sign.svg" alt="icon" />
                      </span>
                      <span>Show Numbers</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input show-numbers"
                          type="checkbox"
                          id="flexSwitchCheckChecked"
                          defaultChecked
                        />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span>
                        <img src="img/table-column.svg" alt="icon" />
                      </span>
                      <span>Reverse Table</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input reverse-table"
                          type="checkbox"
                          id="flexSwitchCheckChecked"
                          defaultChecked
                        />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span>
                        <img src="img/percentage.svg" alt="icon" />
                      </span>
                      <span>Prefer Percentage</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input prefer-percentage"
                          type="checkbox"
                          id="flexSwitchCheckChecked"
                          defaultChecked
                        />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span>
                        <img src="img/show-qt.svg" alt="icon" />
                      </span>
                      <span>Show QT in Lots</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input show-qt"
                          type="checkbox"
                          id="flexSwitchCheckChecked"
                          defaultChecked
                        />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span>
                        <img src="img/trash.svg" alt="icon" />
                      </span>
                      <span>Clear Data Cache</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input clear-data-cache"
                          type="checkbox"
                          id="flexSwitchCheckChecked"
                          defaultChecked
                        />
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ======== Search Nifty popup ======== */}
      <div
        className="modal fade"
        id="searchNiftyModel"
        tabIndex={-1}
        aria-labelledby="searchNiftyModelLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content modal-bg-light-green">
            <div className="modal-body py-4 px-4">
              <form className="search-field-container">
                <div className="form-input">
                  <input
                    type="search"
                    name="Search Nifty"
                    className="form-control"
                    placeholder="Search Stock/Index"
                  />
                </div>
              </form>
              <div className="nifty-index-list">
                <ul className="list-unstyled">
                  <li>
                    <button>Banknifty</button>
                  </li>
                  <li>
                    <button>Finnifty</button>
                  </li>
                  <li>
                    <button>Nifty_Mid_Select</button>
                  </li>
                  <li>
                    <button>Nifty</button>
                  </li>
                  <li>
                    <button>Naturalgas</button>
                  </li>
                  <li>
                    <button>Crudeoil</button>
                  </li>
                  <li>
                    <button>Usdinr</button>
                  </li>
                  <li>
                    <button>Sensex</button>
                  </li>
                  <li>
                    <button>bankex</button>
                  </li>
                  <li>
                    <button>Aartiind</button>
                  </li>
                  <li>
                    <button>Sensex</button>
                  </li>
                  <li>
                    <button>bankex</button>
                  </li>
                  <li>
                    <button>Aartiind</button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ======== OI Percentage Graph popup ======== */}
      <div
        className="modal fade "
        id="oichartModel"
        tabIndex={-1}
        aria-labelledby="oichartModelLabel"
        aria-hidden="true "
      >
        <div className="modal-dialog ai-chart modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue oi-charts-dark">
            <div className="modal-header">
              <h5 className="modal-title" id="oichartModelLabel">
                OI Charts
              </h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <OIChartsPanel instrumentIdentifier={oiChartsInstrument} />
          </div>
        </div>
      </div>
      {/* ======== Spot Chart popup ======== */}
      <SpotChartModal symbol={localStorage.getItem("stock") || "NIFTY"} levels={spotChartLevels} />
      {/* ======== OI Percentage Graph-1 popup ======== */}
      <div
        className="modal fade"
        id="oipercentage1Model"
        tabIndex={-1}
        aria-labelledby="oioipercentage1ModelLabel"
        aria-hidden="true "
      >
        <div className="modal-dialog ai-chart modal-dialog-centered">
          <div className="modal-content" style={{ background: "#131313", border: "1px solid #262626", borderRadius: 8 }}>
            <OIPercentageGraph
              data={oiPctGraph.points || []}
              sessionStart={oiPctGraph.sessionStart}
              sessionEnd={oiPctGraph.sessionEnd}
              loading={oiPctGraphLoading}
              label={oiPercentageGraphCE || oiPercentageGraphPE}
            />
          </div>
        </div>
      </div>
      {/* ======== Combined Price Chart IV- popup ======== */}
      <div
        className="modal fade "
        id="CombinedpriceModel"
        tabIndex={-1}
        aria-labelledby="CombinedpriceModelLabel"
        aria-hidden="true "
      >
        <div className="modal-dialog ai-chart modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-header">
              <h5 className="modal-title" id="CombinedpriceModel">
                Combined Price Chart
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              <div className="oi-chart-btn-inner">
                <ul className="list-unstyled d-flex justify-content-center align-items-center">
                  <li>
                    {" "}
                    <button>
                      {" "}
                      <span className="oi-chart-lite-blue" />
                      Combined Price
                    </button>
                  </li>
                  <li>
                    {" "}
                    <button>
                      {" "}
                      <span className="oi-chart-green" />
                      CE Price
                    </button>
                  </li>
                  <li>
                    {" "}
                    <button>
                      {" "}
                      <span className="oi-chart-red" />
                      PE Price
                    </button>
                  </li>
                </ul>
              </div>
            </div>
            <div className="tab-content" id="pills-tabContent">
              <div
                className="tab-pane fade show active"
                id="pills-home"
                role="tabpanel"
                aria-labelledby="pills-home-tab"
              >
                <div className="ai-chart-img">
                  <img src="img/combined-price-chart-iv.svg" alt="chart" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ======== Combined Price Chart IV-1 popup ======== */}
      <div
        className="modal fade "
        id="futureBuildupModel"
        tabIndex={-1}
        aria-labelledby="futureBuildupModelLabel"
        aria-hidden="true "
      >
        <div className="modal-dialog ai-chart modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-header">
              <h5 className="modal-title" id="futureBuildupModel">
                Future Buildup: Nifty
              </h5>
              <button
                type="button"
                className="info-table"
                data-bs-dismiss="modal"
                data-bs-target="#futureBuildupinfoModel"
              >
                <i style={{ fontSize: "24px" }} className="fa fa-info-circle" />
              </button>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              <div className="oi-chart-btn-inner">
                <ul className="list-unstyled d-flex justify-content-center align-items-center">
                  <li>
                    {" "}
                    <button>
                      {" "}
                      <span className="oi-chart-lite-blue" />
                      Future Price
                    </button>
                  </li>
                  <li>
                    {" "}
                    <button>
                      {" "}
                      <span className="oi-chart-wine" />
                      Future OI Price
                    </button>
                  </li>
                </ul>
              </div>
            </div>
            <div className="tab-content" id="pills-tabContent">
              <div
                className="tab-pane fade show active"
                id="pills-home"
                role="tabpanel"
                aria-labelledby="pills-home-tab"
              >
                <div className="ai-chart-img">
                  <img src="img/combined-price-chart-iv-1.svg" alt="chart" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ======== info-table popup ======== */}
      <div
        className="modal fade "
        id="futureBuildupinfoModel"
        tabIndex={-1}
        aria-labelledby="futureBuildupinfoModelLabel"
        aria-hidden="true "
      >
        <div className="modal-dialog ai-chart modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-body">
              <div className="oi-chart-btn-inner  ">
                <table className="info-table  w-100 ">
                  <tbody>
                    <tr className="info-table-row ">
                      <th className="info-table-heading">Price</th>
                      <th className="info-table-heading">OI or OI CHNG</th>
                      <th className="info-table-heading">Result</th>
                      <th className="info-table-heading">Meaning</th>
                      <th className="info-table-heading">Option Trade</th>
                    </tr>
                    <tr className="info-table-row-1">
                      <td className="info-table-data">Up</td>
                      <td className="info-table-data">Up</td>
                      <td className="info-table-data">Long Buildup</td>
                      <td className="info-table-data">Strong Bullish</td>
                      <td className="info-table-data">CE Buy</td>
                    </tr>
                    <tr className="info-table-row-1">
                      <td className="info-table-data">Up</td>
                      <td className="info-table-data">Up</td>
                      <td className="info-table-data">Long Buildup</td>
                      <td className="info-table-data">Strong Bullish</td>
                      <td className="info-table-data">CE Buy</td>
                    </tr>
                    <tr className="info-table-row-1">
                      <td className="info-table-data">Up</td>
                      <td className="info-table-data">Up</td>
                      <td className="info-table-data">Long Buildup</td>
                      <td className="info-table-data">Strong Bullish</td>
                      <td className="info-table-data">CE Buy</td>
                    </tr>
                    <tr className="info-table-row-1">
                      <td className="info-table-data">Up</td>
                      <td className="info-table-data">Up</td>
                      <td className="info-table-data">Long Buildup</td>
                      <td className="info-table-data">Strong Bullish</td>
                      <td className="info-table-data">CE Buy</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ======== volume-chart popup ======== */}
      <div
        className="modal fade "
        id="volumeChartModel"
        tabIndex={-1}
        aria-labelledby="volumeChartModelLabel"
        aria-hidden="true "
      >
        <div className="modal-dialog  modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-header">
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              <div className="volume-chart-inner  ">
                {/* <h3><strong>Possible Reversal For</strong> : 21500</h3> */}
                <h3>
                  <strong>Possible Reversal For</strong> : {selectedStrikePrice}
                </h3>
                <h4>
                  <strong>Resistance:</strong> {(resistance).toFixed(2)}
                  <button
                    className="volume-copy-btn"
                    data-bs-target="#valueCopyModel"
                    data-bs-dismiss="modal"
                  >
                    <i className="fa fa-clone" aria-hidden="true" />
                  </button>{" "}
                </h4>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className="modal fade "
        id="volumeChartModel12"
        tabIndex={-1}
        aria-labelledby="volumeChartModel12Label"
        aria-hidden="true "
      >
        <div className="modal-dialog  modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-header">
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              <div className="volume-chart-inner  ">
                {/* <h3><strong>Possible Reversal For</strong> : 21500</h3> */}
                <h3>
                  <strong>Possible Reversal For</strong> : {selectedStrikePrice}
                </h3>
                <h4>
                  <strong>Support:</strong> {(support).toFixed(2)}
                  <button
                    className="volume-copy-btn"
                    data-bs-target="#valueCopyModel"
                    data-bs-dismiss="modal"
                  >
                    <i className="fa fa-clone" aria-hidden="true" />
                  </button>{" "}
                </h4>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ======== volume-chart-1 popup ======== */}
      <div
        className="modal fade valueCopy"
        id="valueCopyModel"
        tabIndex={-1}
        aria-labelledby="valueCopyModelLabel"
        aria-hidden="true "
      >
        <div className="modal-dialog  modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-header">
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              <div className="volume-chart-inner volume-chart-1  ">
                <h3>Value Copied to clipboard</h3>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ======== LTP Calculator popup ======== */}
      <div
        className="modal fade "
        id="LPTcalculatorModel"
        tabIndex={-1}
        aria-labelledby="LPTcalculatorModelLabel"
        aria-hidden="true "
      >
        <div className="modal-dialog  modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-header">
              <h5 className="modal-title" id="LPTcalculatorModelLabel">
                LTP calculator 22000 CE
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              <input
                type="text"
                placeholder="Target spot price"
                className="ltp-calculator-field"
              />
              <div className="volume-chart-inner ltp-calculator-content ">
                <h3>
                  <strong>Buy Price:</strong> - 3216.09
                </h3>
                <h4>
                  <strong>SL: </strong> - 3219.09
                </h4>
                <h5>
                  <strong>Target: </strong> - 6527.25
                </h5>
                <p>
                  This is a rough estimate, calculation is based on delta.
                  Please use your own judgement and do not trade based on this
                  alone.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ======== free-user popup ======== */}
      <div
        className="modal fade "
        id="freeUserModel"
        tabIndex={-1}
        aria-labelledby="freeUserModelLabel"
        aria-hidden="true "
      >
        <div className="modal-dialog  modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-header">
              <h5 className="modal-title" id="freeUserModelLabel">
                Upgrade to Pro Plan for <br />
                faster data update
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              <div className="volume-chart-inner  ">
                <div className="free-user-content">
                  <p>As a free user, you will get delayed data by 3 minutes.</p>
                  <p>
                    Upgrade to Pro Plan to get faster data updates and <br />
                    access to all features.
                  </p>
                </div>
                <div className="free-user-check">
                  <h2>
                    <i
                      style={{ color: "#25D366" }}
                      className="fa fa-check-circle-o"
                      aria-hidden="true"
                    >
                      {" "}
                    </i>{" "}
                    Fast data updates
                  </h2>
                  <h2>
                    <i
                      style={{ color: "#25D366" }}
                      className="fa fa-check-circle-o"
                      aria-hidden="true"
                    >
                      {" "}
                    </i>{" "}
                    Ads free experience
                  </h2>
                  <h2>
                    <i
                      style={{ color: "#25D366" }}
                      className="fa fa-check-circle-o"
                      aria-hidden="true"
                    >
                      {" "}
                    </i>{" "}
                    Access to all features
                  </h2>
                  <h2>
                    <i
                      style={{ color: "#25D366" }}
                      className="fa fa-check-circle-o"
                      aria-hidden="true"
                    >
                      {" "}
                    </i>{" "}
                    Access to all future features
                  </h2>
                  <h2>
                    <i
                      style={{ color: "#25D366" }}
                      className="fa fa-check-circle-o"
                      aria-hidden="true"
                    >
                      {" "}
                    </i>{" "}
                    Premium support
                  </h2>
                </div>
                <div className="free-user-btn-content d-flex justify-content-center align-items-center">
                  <button className="free-user-btn me-3">
                    Continue as free user
                  </button>
                  <button className="free-user-btn btn-red">
                    Subscribe to Pro Plan
                  </button>
                </div>
              </div>zz
            </div>
          </div>
        </div>
      </div>
      {/* ======== Search Nifty popup ======== */}
      <div
        className="modal fade"
        id="searchNiftyModel"
        tabIndex={-1}
        aria-labelledby="searchNiftyModelLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content modal-bg-light-green">
            <div className="modal-body py-4 px-4">
              <form className="search-field-container">
                <div className="form-input">
                  <input
                    type="search"
                    name="Search Nifty"
                    className="form-control"
                    placeholder="Search Stock/Index"
                  />
                </div>
              </form>
              <div className="nifty-index-list">
                <ul className="list-unstyled">
                  <li>
                    <button>Banknifty</button>
                  </li>
                  <li>
                    <button>Finnifty</button>
                  </li>
                  <li>
                    <button>Nifty_Mid_Select</button>
                  </li>
                  <li>
                    <button>Nifty</button>
                  </li>
                  <li>
                    <button>Naturalgas</button>
                  </li>
                  <li>
                    <button>Crudeoil</button>
                  </li>
                  <li>
                    <button>Usdinr</button>
                  </li>
                  <li>
                    <button>Sensex</button>
                  </li>
                  <li>
                    <button>bankex</button>
                  </li>
                  <li>
                    <button>Aartiind</button>
                  </li>
                  <li>
                    <button>Sensex</button>
                  </li>
                  <li>
                    <button>bankex</button>
                  </li>
                  <li>
                    <button>Aartiind</button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ======== Update Profile popup ======== */}
      <div
        className="modal fade"
        id="updateModel"
        tabIndex={-1}
        aria-labelledby="updateModelLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-header">
              <h5 className="modal-title" id="disclaimerModelLabel">
                Update Profile
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body py-4 px-5">
              <div className="header-logo text-center">
                <a href="#">
                  <img src="img/header-logo.svg" alt="logo" />
                </a>
              </div>
              <div className="login-form-outer">
                <form>
                  <div className="form-input mb-3">
                    <input
                      type="text"
                      name="Name"
                      className="form-control"
                      placeholder="Name"
                    />
                  </div>
                  <div className="form-input mb-3">
                    <input
                      type="text"
                      name="State"
                      className="form-control"
                      placeholder="State"
                    />
                  </div>
                  <div className="form-input mb-3">
                    <input
                      type="text"
                      name="District"
                      className="form-control"
                      placeholder="District"
                    />
                  </div>
                  <div className="form-input mb-3">
                    <input
                      type="text"
                      name="City"
                      className="form-control"
                      placeholder="City"
                    />
                  </div>
                  <div className="form-input mb-3">
                    <input
                      type="text"
                      name="Phone"
                      className="form-control"
                      placeholder="Phone"
                    />
                  </div>
                  <div className="update-btn-outer">
                    <button
                      type="button"
                      className="site-btn btn-blue-o"
                      data-bs-dismiss="modal"
                      aria-label="Close"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="update-btn site-btn btn-blue"
                    >
                      Update
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ======== Delete Account Warning popup ======== */}
      <div
        className="modal fade"
        id="deleteACModel"
        tabIndex={-1}
        aria-labelledby="deleteACModelLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content modal-bg-light-green">
            <div className="modal-body py-4 px-5 text-center">
              <h4>Warning !!</h4>
              <p className="desc my-2">
                Are you sure you want to delete your account?
              </p>
              <p className="desc mb-2">This action cannot be done.</p>
              <p className="desc mb-2">All your data will be deleted.</p>
              <div className="donate-btn-grp text-center py-4">
                <button
                  className="site-btn btn-blue delete-btn"
                  data-bs-toggle="modal"
                  data-bs-target="#deleteACModel"
                >
                  Cancel
                </button>
                <button className="site-btn btn-red delete-btn">
                  Delete Account
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ======== Notification popup ======== */}
      <div
        className="modal fade"
        id="notificationModel"
        tabIndex={-1}
        aria-labelledby="notificationModelLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-header">
              <h5 className="modal-title" id="notificationModelLabel">
                Notification Setting
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              <div className="notification-functionalty-outer popup-list">
                <ul className="list-unstyled">
                  <li>
                    <div className="popup-list-title">
                      <span>
                        <img src="img/stock-ticker.svg" alt="icon" />
                      </span>
                      <span>Stocks</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input stocks"
                          type="checkbox"
                          id="flexSwitchCheckChecked"
                          defaultChecked
                        />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span>
                        <img src="img/data-collection.svg" alt="icon" />
                      </span>
                      <span>Indices</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input indices"
                          type="checkbox"
                          id="flexSwitchCheckChecked"
                          defaultChecked
                        />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span>
                        <img src="img/currency.svg" alt="icon" />
                      </span>
                      <span>Currency</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input currency"
                          type="checkbox"
                          id="flexSwitchCheckChecked"
                          defaultChecked
                        />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span>
                        <img src="img/stock_mcx.svg" alt="icon" />
                      </span>
                      <span>MCX</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input mcx"
                          type="checkbox"
                          id="flexSwitchCheckChecked"
                          defaultChecked
                        />
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ======== Disclaimer popup ======== */}
      <div
        className="modal fade"
        id="disclaimerModel"
        tabIndex={-1}
        aria-labelledby="disclaimerModelLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-header">
              <h5 className="modal-title" id="disclaimerModelLabel">
                Disclaimer
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              <div className="disclaimer-functionalty-outer">
                <p>
                  Lorem Ipsum is simply dummy text of the printing and
                  typesetting industry. Lorem Ipsum has been the industry's
                  standard dummy text ever since the 1500s, when an unknown
                  printer took a galley of type and scrambled it to make a type
                  specimen book. It has survived not only five centuries, but
                  also the leap into electronic typesetting, remaining
                  essentially unchanged. It was popularised in the 1960s with
                  the release of Letraset sheets containing Lorem Ipsum
                  passages, and more recently with desktop publishing software
                  like Aldus PageMaker including versions of Lorem Ipsum
                  <br />
                  Lorem Ipsum is simply dummy text of the printing and
                  typesetting industry. Lorem Ipsum has been the industry's
                  standard dummy text ever since the 1500s, when an unknown
                  printer took a galley of type and scrambled it to make a type
                  specimen book. It has survived not only five centuries, but
                  also the leap into electronic typesetting, remaining
                  essentially unchanged.{" "}
                </p>
                <div className="modal-footer mt-3">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    data-bs-dismiss="modal"
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary site-btn"
                    data-bs-dismiss="modal"
                  >
                    Accept &amp; Continue
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ======== Login popup ======== */}
      <div
        className="modal fade"
        id="loginModel"
        tabIndex={-1}
        aria-labelledby="loginModelLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-body py-4 px-5">
              <div className="header-logo text-center">
                <a href="#">
                  <img src="img/header-logo.svg" alt="logo" />
                </a>
              </div>
              <div className="login-title text-center my-3">
                <h4>Login</h4>
                <h5>
                  Welcome to Option Trading Wala <br /> Please Login to Continue
                </h5>
              </div>
              <div className="login-form-outer">
                <form>
                  <div className="form-input mb-3">
                    <label
                      htmlFor="exampleFormControlInput1"
                      className="form-label"
                    >
                      Email / Phone No
                    </label>
                    <input
                      type="text"
                      name="Email or Phone No"
                      className="form-control"
                      placeholder="Enter Email or Phone No."
                    />
                  </div>
                  <div className="form-input mb-3">
                    <label
                      htmlFor="exampleFormControlInput2"
                      className="form-label"
                    >
                      Password
                    </label>
                    <input
                      type="password"
                      name="Password"
                      className="form-control"
                      placeholder="Enter Password"
                    />
                  </div>
                  <div className="otp-outer text-center">
                    <p>Enter your OTP</p>
                    <div className="otp-code-inner my-2">
                      <input type="text" name="otp-code-1" maxLength={1} />
                      <input type="text" name="otp-code-2" maxLength={1} />
                      <input type="text" name="otp-code-3" maxLength={1} />
                      <input type="text" name="otp-code-4" maxLength={1} />
                      <input type="text" name="otp-code-5" maxLength={1} />
                      <input type="text" name="otp-code-6" maxLength={1} />
                    </div>
                    <p>
                      <a href="#" className="text-success">
                        Resend OTP In
                      </a>
                      <span className="otp-time ms-2">00:28</span>
                    </p>
                  </div>
                  <button type="submit" className="submit-btn">
                    Login
                  </button>
                </form>
                <div className="signup-and-forgot-pswd d-flex justify-content-between mt-2">
                  <a href="#" className="text-danger">
                    SignUp
                  </a>
                  <a href="#">Forget Password?</a>
                </div>
                <div className="privacy-policy-and-terms-&-service text-center mt-4">
                  <p>
                    By Clicking on login you agree to <br />
                    <a href="#">Privacy Policy</a> &amp;{" "}
                    <a href="#">Terms &amp; Service</a>
                  </p>
                </div>
                <div className="google-sign-in text-center">
                  <button>
                    <img src="img/google-logo.svg" alt="google-logo" />
                    Sign in with Google
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ======== Strike price popup ======== */}
      <div
        className="modal fade"
        id="strikePriceModel"
        tabIndex={-1}
        aria-labelledby="strikePriceModelLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-header">
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <div className="volume-chart-inner">
                <h5>Elastic Ends Of Strike Price</h5>
                <br />
                <h2>
                  <strong>{selectedStrikePrice}</strong>
                </h2>
                <div className="modal-info-section">
                  <ul
                    className="nav nav-tabs justify-content-center"
                    id="myTab"
                    role="tablist"
                  >
                    <li className="nav-item">
                      <a
                        className="nav-link active"
                        id="home-tab"
                        data-bs-toggle="tab"
                        href="#home"
                        role="tab"
                        aria-controls="home"
                        aria-selected="true"
                      >
                        SPOT
                      </a>
                    </li>
                    <li className="nav-item">
                      <a

                        className="nav-link"
                        id="profile-tab"
                        data-bs-toggle="tab"
                        href="#profile"
                        role="tab"
                        aria-controls="profile"
                        aria-selected="false"
                      >
                        FUTURE
                      </a>
                    </li>
                    {/* <li className="nav-item">
                      <a
                        className="nav-link"
                        id="contact-tab"
                        data-bs-toggle="tab"
                        href="#contact"
                        role="tab"
                        aria-controls="contact"
                        aria-selected="false"
                      >
                        WEEKLY FUTURE
                      </a>
                    </li> */}
                  </ul>
                </div>

                <div className="tab-content" id="myTabContent">
                  <div
                    className="tab-pane fade show active"
                    id="home"
                    role="tabpanel"
                    aria-labelledby="home-tab"
                  >
                    <div className="modal-info-section">
                      <h5>Average Elastic Ends(9:30 AM)</h5>
                      <h2>
                        <span className="text-danger">Resistance:</span>{" "}
                        {resistance2.toFixed(2)}
                        <button
                          className="volume-copy-btn"
                          data-bs-toggle="modal"
                          data-bs-target="#valueCopyModel"
                        >
                          <i className="fa fa-clone" aria-hidden="true"></i>
                        </button>
                      </h2>
                      <h2>
                        <span className="text-success">Support:</span>{" "}
                        {support2.toFixed(2)}
                        <button
                          className="volume-copy-btn"
                          data-bs-toggle="modal"
                          data-bs-target="#valueCopyModel"
                        >
                          <i className="fa fa-clone" aria-hidden="true"></i>
                        </button>
                      </h2>
                    </div>
                    <div className="modal-info-section">
                      <h5>Current Elastic Ends</h5>
                      <h2>
                        <span className="text-danger">Resistance:</span>{" "}
                        {resistance.toFixed(2)}
                        <button
                          className="volume-copy-btn"
                          data-bs-toggle="modal"
                          data-bs-target="#valueCopyModel"
                        >
                          <i className="fa fa-clone" aria-hidden="true"></i>
                        </button>
                      </h2>
                      <h2>
                        <span className="text-success">Support:</span>{support.toFixed(2)}
                        <button
                          className="volume-copy-btn"
                          data-bs-toggle="modal"
                          data-bs-target="#valueCopyModel"
                        >
                          <i className="fa fa-clone" aria-hidden="true"></i>
                        </button>
                      </h2>
                    </div>
                  </div>

                  <div
                    className="tab-pane fade"
                    id="profile"
                    role="tabpanel"
                    aria-labelledby="profile-tab"
                  >
                    <div className="modal-info-section">
                      <h5>Average Elastic Ends(9:30 AM)</h5>
                      <h2>
                        <span className="text-danger">Resistance:</span>{futureResistance2.toFixed(2)}

                        <button
                          className="volume-copy-btn"
                          data-bs-toggle="modal"
                          data-bs-target="#valueCopyModel"
                        >
                          <i className="fa fa-clone" aria-hidden="true"></i>
                        </button>
                      </h2>
                      <h2>
                        <span className="text-success">Support:</span>{futureSupport2.toFixed(2)}
                        <button
                          className="volume-copy-btn"
                          data-bs-toggle="modal"
                          data-bs-target="#valueCopyModel"
                        >
                          <i className="fa fa-clone" aria-hidden="true"></i>
                        </button>
                      </h2>
                    </div>
                    <div className="modal-info-section">
                      <h5>Current Elastic Ends</h5>
                      <h2>
                        <span className="text-danger">Resistance:</span>{futureResistance.toFixed(2)}
                        <button
                          className="volume-copy-btn"
                          data-bs-toggle="modal"
                          data-bs-target="#valueCopyModel"
                        >
                          <i className="fa fa-clone" aria-hidden="true"></i>
                        </button>
                      </h2>
                      <h2>
                        <span className="text-success">Support:</span> {futureSupport.toFixed(2)}
                        <button
                          className="volume-copy-btn"
                          data-bs-toggle="modal"
                          data-bs-target="#valueCopyModel"
                        >
                          <i className="fa fa-clone" aria-hidden="true"></i>
                        </button>
                      </h2>
                    </div>
                  </div>
                  {/* <div
                    className="tab-pane fade"
                    id="contact"
                    role="tabpanel"
                    aria-labelledby="contact-tab"
                  >
                    <div className="modal-info-section">
                      <h5>Average Elastic Ends(9:15 AM)</h5>
                      <h2>
                        <span className="text-danger">Resistance:</span>{" "}
                        24573.55
                        <button
                          className="volume-copy-btn"
                          data-bs-toggle="modal"
                          data-bs-target="#valueCopyModel"
                        >
                          <i className="fa fa-clone" aria-hidden="true"></i>
                        </button>
                      </h2>
                      <h2>
                        <span className="text-success">Support:</span>{" "}
                        24941.8083
                        <button
                          className="volume-copy-btn"
                          data-bs-toggle="modal"
                          data-bs-target="#valueCopyModel"
                        >
                          <i className="fa fa-clone" aria-hidden="true"></i>
                        </button>
                      </h2>
                    </div>
                    <div className="modal-info-section">
                      <h5>Current Elastic Ends</h5>
                      <h2>
                        <span className="text-danger">Resistance:</span>{" "}
                        25065.35
                        <button
                          className="volume-copy-btn"
                          data-bs-toggle="modal"
                          data-bs-target="#valueCopyModel"
                        >
                          <i className="fa fa-clone" aria-hidden="true"></i>
                        </button>
                      </h2>
                      <h2>
                        <span className="text-success">Support:</span> 25007.11
                        <button
                          className="volume-copy-btn"
                          data-bs-toggle="modal"
                          data-bs-target="#valueCopyModel"
                        >
                          <i className="fa fa-clone" aria-hidden="true"></i>
                        </button>
                      </h2>
                    </div>
                  </div> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/*====== Call vs Put OI===== */}
      <div
        className="modal fade"
        id="callVsPutOI"
        tabIndex={-1}
        aria-labelledby="callVsPutOILabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-header">
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              <div className="volume-chart-inner">

                <div className="row mb-6">
                  <div className="col">
                    <label htmlFor="rangeStart" className="form-label">Range Start</label>
                    <select
                      id="rangeStart"
                      className="form-select"
                      value={rangeStart}
                      onChange={(e) => setRangeStart(e.target.value)}
                    >
                      {rangeOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col">
                    <label htmlFor="rangeEnd" className="form-label">Range End</label>
                    <select
                      id="rangeEnd"
                      className="form-select"
                      value={rangeEnd}
                      onChange={(e) => setRangeEnd(e.target.value)}
                    >
                      {rangeOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mt-4">
                  <h5 className="text-center">Change in Open Interest(COI)</h5>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={dataCallPutOIChange} barCategoryGap={30}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis tickFormatter={(val) => `${val}L`} />
                      <Tooltip formatter={(value) => `${value}L`} />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]} barSize={30}>
                        {dataCallPutOIChange.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.name === "CALL" ? "red" : "green"}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>



                </div>
                <div className="mt-4">
                  <h5 className="text-center"> Open Interest(OI)</h5>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={dataCallPutOI} barCategoryGap={30}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis tickFormatter={(val) => `${val}L`} />
                      <Tooltip formatter={(value) => `${value}L`} />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]} barSize={30}>
                        {dataCallPutOI.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.name === "CALL" ? "red" : "green"} // Red for CALL, Green for PUT
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>

                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
      {/* end code */}
      {/*Top 5 openinterestchange  */}
      <div
        className="modal fade"
        id="top5openinterestchnage"
        tabIndex={-1}
        aria-labelledby="top5openinterestchnageLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-body">
              <h5 className="modal-title text-center mb-3" id="top5openinterestchnageLabel">
                Top 5 Open Interest Change
              </h5>
              <ul className="list-group">
                {top5OI.map((item, index) => (
                  <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                    <span>Strike Price: {item.strikePrice}</span>
                    <span>{item.type} - Change: {item.change}</span>
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>
      </div>
      {/* end code top 5 */}
      {/*call and put writing  */}
      <div
        className="modal fade"
        id="callAndPutWriting"
        tabIndex={-1}
        aria-labelledby="callAndPutWritingLabel"

      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-body">
              <h5 className="modal-title text-center mb-3" id="callAndPutWritingLabel">
                Call Writing And Put Writing
              </h5>
              <h5 style={{
                color: cumulativeCallOIChange > cumulativePutOIChange
                  ? "red"
                  : cumulativePutOIChange > cumulativeCallOIChange
                    ? "green"
                    : "black"
              }}>
                {cumulativeCallOIChange > cumulativePutOIChange
                  ? "1.TOTAL: CW > PW"
                  : cumulativePutOIChange > cumulativeCallOIChange
                    ? "1.TOTAL: PW > CW"
                    : "1.TOTAL: CW = PW"}
              </h5>

              <h5 style={{
                color: totalPE_1 > totalCE_1
                  ? "green"
                  : totalCE_1 > totalPE_1
                    ? "red"
                    : "black"
              }}><br />
                2.NEAREST STRIKE: {totalPE_1 > totalCE_1
                  ? "PW > CW"
                  : totalCE_1 > totalPE_1
                    ? "CW > PW"
                    : "PW and CE = CW"}
              </h5>

              <h5 style={{
                color: totalPE_2 > totalCE_2
                  ? "green"
                  : totalCE_2 > totalPE_2
                    ? "red"
                    : "black"
              }}><br />
                3. TWO NEAREST STRIKE: {totalPE_2 > totalCE_2
                  ? "PW > CW"
                  : totalCE_2 > totalPE_2
                    ? "CW > PW"
                    : "PW and CE = CW"}
              </h5>

            </div>

          </div>
        </div>
      </div>
      {/* end code top 5 */}
      {/* future oi graph click on spot price */}
      <div
        className="modal fade"
        id="futurpricegraph"
        tabIndex={-1}
        aria-labelledby="futurpricegraphLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-xl" style={{ maxWidth: '95vw', maxHeight: '90vh' }}>
          <div className="modal-content modal-bg-light-blue" style={{ height: '90vh' }}>
            <div className="modal-header">
              <h5 className="modal-title" id="futurpricegraphLabel">Future Price Graph</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body" style={{ height: '100%', overflowY: 'auto' }}>
              {/* Toggle Buttons */}
              <div className="d-flex gap-2 mb-3 justify-content-center">
                <button
                  className={`btn btn-sm ${showFutureOI ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setShowFutureOI(!showFutureOI)}
                >
                  Future OI
                </button>
                <button
                  className={`btn btn-sm ${showFuturePrice ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setShowFuturePrice(!showFuturePrice)}
                >
                  Future Price
                </button>
                <button
                  className={`btn btn-sm ${showSpotPrice ? 'btn-success' : 'btn-outline-success'}`}
                  onClick={() => setShowSpotPrice(!showSpotPrice)}
                >
                  Spot Price
                </button>
                <button
                  className={`btn btn-sm ${showFutureOIChange ? 'btn-secondary' : 'btn-outline-secondary'}`}
                  onClick={() => setShowFutureOIChange(!showFutureOIChange)}
                >
                  Future OI Chng
                </button>
              </div>

              {/* Chart */}
              {futureData.length > 0 ? (
                <div style={{ height: '80vh' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={futureData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="time" />

                      {/* Left Y-Axis for Future OI (scaled in Millions) */}
                      <YAxis
                        yAxisId="left"
                        orientation="left"
                        stroke="purple"
                        domain={['auto', 'auto']}
                        tickFormatter={formatCompact}
                      />

                      {/* Left Y-Axis for Future OI change (its own scale: it can be negative) */}
                      {showFutureOIChange && (
                        <YAxis
                          yAxisId="oiChange"
                          orientation="left"
                          stroke="#9c27b0"
                          domain={['auto', 'auto']}
                          tickFormatter={formatCompact}
                        />
                      )}

                      {/* Right Y-Axis for Spot Price and Future Price */}
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        stroke="blue"
                        domain={['auto', 'auto']}
                        tickFormatter={formatCompact}
                      />

                      <Tooltip formatter={(value, name) => [formatCompact(value), name]} />
                      <Legend />

                      {/* Spot Price */}
                      {showSpotPrice && (
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="spotPrice"
                          stroke="green"
                          strokeWidth={2}
                          dot={false}
                          name="Spot Price"
                        />
                      )}

                      {/* Future Price */}
                      {showFuturePrice && (
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="futurePrice"
                          stroke="blue"
                          strokeWidth={2}
                          dot={false}
                          name="Future Price"
                        />
                      )}

                      {/* Future OI */}
                      {showFutureOI && (
                        <Line
                          yAxisId="left"
                          type="monotone"
                          dataKey="futureOI"
                          stroke="purple"
                          strokeWidth={2}
                          dot={false}
                          name="Future OI"
                        />
                      )}

                      {/* Future OI change */}
                      {showFutureOIChange && (
                        <Line
                          yAxisId="oiChange"
                          type="monotone"
                          dataKey="futureOIChange"
                          stroke="#9c27b0"
                          strokeDasharray="4 2"
                          strokeWidth={2}
                          dot={false}
                          name="Future OI Chng"
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p>Loading........</p>
              )}



            </div>
          </div>
        </div>
      </div>



      {/* end future oi graph */}
      {/* ======== Signup popup ======== */}
      <div
        className="modal fade"
        id="signupModel"
        tabIndex={-1}
        aria-labelledby="signupModelLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-body py-4 px-5">
              <div className="header-logo text-center">
                <a href="#">
                  <img src="img/header-logo.svg" alt="logo" />
                </a>
              </div>
              <div className="login-title text-center my-3">
                <h4>Register</h4>
                <h5>
                  Welcome to Option Trading Wala <br /> Please Register to
                  Continue
                </h5>
              </div>
              <div className="login-form-outer">
                <form>
                  <div className="form-input mb-3">
                    <label
                      htmlFor="exampleFormControlInput1"
                      className="form-label"
                    >
                      Name
                    </label>
                    <input
                      type="text"
                      name="Name"
                      className="form-control"
                      placeholder="Enter Name"
                    />
                  </div>
                  <div className="form-input mb-3">
                    <label
                      htmlFor="exampleFormControlInput2"
                      className="form-label"
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      name="Email"
                      className="form-control"
                      placeholder="Enter Email"
                    />
                  </div>
                  <div className="form-input mb-3">
                    <label
                      htmlFor="exampleFormControlInput3"
                      className="form-label"
                    >
                      Phone No.
                    </label>
                    <input
                      type="tel"
                      name="phone no."
                      className="form-control"
                      placeholder="Enter Phone No."
                    />
                  </div>
                  <div className="form-input mb-3">
                    <label
                      htmlFor="exampleFormControlInput4"
                      className="form-label"
                    >
                      Password
                    </label>
                    <input
                      type="password"
                      name="Password"
                      className="form-control"
                      placeholder="Enter Password"
                    />
                  </div>
                  <div className="otp-outer text-center">
                    <p>Enter your OTP</p>
                    <div className="otp-code-inner my-2">
                      <input type="text" name="otp-code-1" maxLength={1} />
                      <input type="text" name="otp-code-2" maxLength={1} />
                      <input type="text" name="otp-code-3" maxLength={1} />
                      <input type="text" name="otp-code-4" maxLength={1} />
                      <input type="text" name="otp-code-5" maxLength={1} />
                      <input type="text" name="otp-code-6" maxLength={1} />
                    </div>
                    <p>
                      <a href="#" className="text-success">
                        Resend OTP In
                      </a>
                      <span className="otp-time ms-2">00:28</span>
                    </p>
                  </div>
                  <button type="submit" className="submit-btn">
                    Login
                  </button>
                </form>
                <div className="signup-and-forgot-pswd d-flex justify-content-center mt-2">
                  <p>
                    Already have an Account?{" "}
                    <a href="#" className="text-danger">
                      Login
                    </a>
                  </p>
                </div>
                <div className="privacy-policy-and-terms-&-service text-center mt-4">
                  <p>
                    By Clicking on login you agree to <br />
                    <a href="#">Privacy Policy</a> &amp;{" "}
                    <a href="#">Terms &amp; Service</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Optionchain;
